/**
 * @fileoverview Signals Page - "The Watchtower"
 * Live macro intelligence dashboard displaying 120 proprietary signals
 * from the capitulators.io API.
 * 
 * Features:
 * - Category tabs for filtering
 * - Search functionality
 * - Stress heatmap grid
 * - Signal strength meter
 * - Sparkline charts on each card
 * 
 * @module pages/signals
 */

import { el } from '../shared/components/simpleRender.js';
import {
  fetchSignalHistory,
  preloadAllSignals,
  SIGNAL_IDS,
  ALL_SIGNAL_IDS,
  CATEGORIES,
  CATEGORY_ORDER,
  getLevelColor,
  SIGNAL_METADATA,
  isCapComputed,
  getSignalFormula,
  hasHistoryEndpoint,
} from '../api/capitulators.js';
import {
  createSignalCard,
  createSignalCardPlaceholder,
  destroySignalCard,
  createCategorySection,
  createCompositeGauge,
  updateCompositeGauge,
  getGaugeStatus,
  openExpandedChartModal,
} from '../signals/components/index.js';
import { updateCategorySection } from '../signals/components/CategorySection.js';
import {
  getSignalDoc,
  hasSignalDoc,
  getTierLabel,
  getRelatedSignals,
} from '../data/signalDocumentation.js';
import { createCSIWidget } from '../csi/components/index.js';

/**
 * Page state
 */
let pageState = {
  signals: {},
  flatSignals: {},
  history: {},
  loading: true,
  error: null,
  lastUpdated: null,
  activeCategory: 'all',
  searchQuery: '',
  stressLevelFilter: null, // New: filter by stress level (null = all, or 'stressed'|'elevated'|'normal'|'calm')
};

/** @type {{element: HTMLElement, update: Function, destroy: Function}|null} */
let csiWidgetInstance = null;

/** @type {{element: HTMLElement, update: Function, destroy: Function}|null} */
let csiHistoryChartInstance = null;

// ============================================================================
// HEATMAP TOOLTIP SYSTEM
// ============================================================================

let heatmapTooltip = null;
let tooltipTimeout = null;
const TOOLTIP_DELAY = 200; // ms before showing

// Signal Card Tooltip System (Phase 5)
let cardTooltip = null;
let cardTooltipTimeout = null;
const CARD_TOOLTIP_DELAY = 300; // slightly longer delay for cards

/**
 * Gets sparkline color based on stress level
 * @param {string} level - Signal level (calm, low, normal, elevated, high, stressed, extreme, crisis)
 * @returns {string} CSS color value
 */
function getSparklineColorForLevel(level) {
  const colors = {
    calm: 'var(--tg-fog-green)',
    low: 'var(--tg-fog-green)',
    normal: 'var(--tg-liquidity-cyan)',
    tight: 'var(--tg-fog-green)',
    elevated: '#f97316',
    wide: '#f97316',
    flat: '#f97316',
    high: 'var(--tg-rust-red)',
    stressed: 'var(--tg-rust-red)',
    inverted: 'var(--tg-rust-red)',
    extreme: 'var(--tg-flash-crash)',
    crisis: 'var(--tg-flash-crash)',
  };
  return colors[level] || 'var(--tg-liquidity-cyan)';
}

/**
 * Renders a mini sparkline in the tooltip
 * @param {HTMLElement} container - Container element for sparkline
 * @param {Array} historyData - Array of { date, value } objects
 * @param {string} level - Signal stress level for coloring
 */
function renderTooltipSparkline(container, historyData, level) {
  container.innerHTML = '';
  
  if (!historyData || historyData.length < 2) {
    container.innerHTML = '<span class="sparkline-empty">No history</span>';
    return;
  }
  
  // Use last 30 points max, convert to chart format
  const data = historyData
    .slice(-30)
    .map(d => ({
      time: new Date(d.date).getTime() / 1000,
      value: parseFloat(d.value)
    }))
    .filter(d => !isNaN(d.value))
    .sort((a, b) => a.time - b.time);
  
  if (data.length < 2) {
    container.innerHTML = '<span class="sparkline-empty">Insufficient data</span>';
    return;
  }
  
  const width = 196; // Container width minus padding
  const height = 36;
  const padding = { top: 4, right: 4, bottom: 4, left: 4 };
  
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Calculate scales
  const values = data.map(d => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  const yPadding = range * 0.1;
  const yMin = minVal - yPadding;
  const yMax = maxVal + yPadding;
  const yRange = yMax - yMin;
  
  // Generate points
  const points = data.map((d, i) => {
    const x = padding.left + (i / (data.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - ((d.value - yMin) / yRange) * chartHeight;
    return { x, y };
  });
  
  // Build SVG path
  const pathD = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
  ).join(' ');
  
  // Area path for subtle fill
  const areaD = pathD + 
    ` L ${points[points.length - 1].x.toFixed(1)} ${height - padding.bottom}` +
    ` L ${points[0].x.toFixed(1)} ${height - padding.bottom} Z`;
  
  const lineColor = getSparklineColorForLevel(level);
  
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'tooltip-sparkline');
  svg.setAttribute('width', width);
  svg.setAttribute('height', height);
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.style.display = 'block';
  
  svg.innerHTML = `
    <path d="${areaD}" fill="${lineColor}" fill-opacity="0.15" stroke="none"/>
    <path d="${pathD}" fill="none" stroke="${lineColor}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="${points[points.length - 1].x.toFixed(1)}" cy="${points[points.length - 1].y.toFixed(1)}" r="2.5" fill="${lineColor}"/>
  `;
  
  container.appendChild(svg);
}

/**
 * Calculates change from history data
 * @param {Array} historyData - Array of { date, value } objects
 * @param {number} periods - Number of periods to look back (default 7 days)
 * @returns {Object|null} Change object with absolute, percent, direction, periods
 */
function calculateChange(historyData, periods = 7) {
  if (!historyData || historyData.length < 2) return null;
  
  // Sort by date descending to get most recent first
  const sorted = [...historyData]
    .map(d => ({ date: new Date(d.date), value: parseFloat(d.value) }))
    .filter(d => !isNaN(d.value))
    .sort((a, b) => b.date - a.date);
  
  if (sorted.length < 2) return null;
  
  const latest = sorted[0].value;
  const priorIndex = Math.min(periods, sorted.length - 1);
  const prior = sorted[priorIndex].value;
  
  if (prior === 0) return null;
  
  const absolute = latest - prior;
  const percent = ((latest - prior) / Math.abs(prior)) * 100;
  const direction = absolute > 0.001 ? 'up' : absolute < -0.001 ? 'down' : 'flat';
  
  // Calculate actual days between measurements
  const actualDays = Math.round((sorted[0].date - sorted[priorIndex].date) / (1000 * 60 * 60 * 24));
  
  return { absolute, percent, direction, periods: actualDays || periods };
}

/**
 * Formats change for display in tooltip
 * @param {Object} change - Change object from calculateChange
 * @returns {string} HTML string for change display
 */
function formatChange(change) {
  if (!change) return '';
  
  const arrow = change.direction === 'up' ? '▲' : change.direction === 'down' ? '▼' : '→';
  const sign = change.percent >= 0 ? '+' : '';
  
  let colorClass;
  if (change.direction === 'up') {
    colorClass = 'heatmap-tooltip__change--up';
  } else if (change.direction === 'down') {
    colorClass = 'heatmap-tooltip__change--down';
  } else {
    colorClass = 'heatmap-tooltip__change--flat';
  }
  
  return `<span class="${colorClass}">${arrow} ${sign}${change.percent.toFixed(1)}%</span> <span class="heatmap-tooltip__change-period">(${change.periods}d)</span>`;
}

/**
 * Calculates the percentile rank of the current value within historical data
 * @param {number} currentValue - Current signal value
 * @param {Array} historyData - Array of { date, value } objects
 * @returns {number|null} Percentile (0-100) or null if insufficient data
 */
function calculatePercentile(currentValue, historyData) {
  if (!historyData || historyData.length < 10) return null;
  if (currentValue == null) return null;
  
  // Get all valid values sorted
  const values = historyData
    .map(d => parseFloat(d.value))
    .filter(v => !isNaN(v))
    .sort((a, b) => a - b);
  
  if (values.length < 10) return null;
  
  // Count how many values are less than or equal to current
  const rank = values.filter(v => v <= currentValue).length;
  const percentile = Math.round((rank / values.length) * 100);
  
  return percentile;
}

/**
 * Renders percentile context HTML
 * @param {number} percentile - Percentile value (0-100)
 * @returns {string} HTML string for percentile display
 */
function renderPercentileHTML(percentile) {
  if (percentile === null) return '';
  
  return `
    <div class="heatmap-tooltip__percentile">
      <div class="heatmap-tooltip__percentile-text">
        Higher than <strong>${percentile}%</strong> of historical values
      </div>
      <div class="tooltip-percentile-bar">
        <div class="tooltip-percentile-bar__fill" style="width: 100%"></div>
        <div class="tooltip-percentile-bar__marker" style="left: ${percentile}%"></div>
      </div>
    </div>
  `;
}

/**
 * Creates the heatmap tooltip DOM structure
 * Enhanced with signal documentation support
 * @returns {HTMLElement}
 */
function createHeatmapTooltip() {
  return el('div', { className: 'heatmap-tooltip', 'data-component': 'heatmap-tooltip' }, [
    el('div', { className: 'heatmap-tooltip__header' }, [
      el('span', { className: 'heatmap-tooltip__abbr' }),
      el('span', { className: 'heatmap-tooltip__tier' }),
      el('span', { className: 'heatmap-tooltip__status' }),
    ]),
    el('div', { className: 'heatmap-tooltip__name' }),
    el('div', { className: 'heatmap-tooltip__description' }),
    el('div', { className: 'heatmap-tooltip__sparkline' }),
    el('div', { className: 'heatmap-tooltip__value-row' }, [
      el('span', { className: 'heatmap-tooltip__value' }),
      el('span', { className: 'heatmap-tooltip__change' }),
    ]),
    el('div', { className: 'heatmap-tooltip__interpretation' }),
    el('div', { className: 'heatmap-tooltip__key-levels' }),
    el('div', { className: 'heatmap-tooltip__context' }),
    el('div', { className: 'heatmap-tooltip__formula' }),
    el('div', { className: 'heatmap-tooltip__cta' }, ['Click to expand →']),
  ]);
}

/**
 * Gets interpretation text based on signal level and documentation
 * @param {Object} signalDoc - Signal documentation object
 * @param {string} level - Current signal level
 * @returns {string} Interpretation text
 */
function getInterpretationForLevel(signalDoc, level) {
  if (!signalDoc?.interpretation) return '';
  
  const interp = signalDoc.interpretation;
  const levelMap = {
    'calm': 'stable',
    'low': 'stable',
    'normal': 'stable',
    'tight': 'stable',
    'elevated': 'rising',
    'wide': 'rising',
    'flat': 'flat',
    'high': 'rising',
    'stressed': 'rising',
    'inverted': 'falling',
    'extreme': 'rising',
    'crisis': 'rising',
  };
  
  const direction = levelMap[level] || 'stable';
  return interp[direction] || interp.stable || interp.rising || '';
}

/**
 * Truncates text to a maximum length
 * @param {string} text - Text to truncate
 * @param {number} maxLen - Maximum length
 * @returns {string}
 */
function truncateText(text, maxLen = 120) {
  if (!text || text.length <= maxLen) return text || '';
  return text.slice(0, maxLen).trim() + '...';
}

/**
 * Updates tooltip content with signal data and documentation
 * @param {Object} signal - Heatmap signal data
 */
function updateTooltipContent(signal) {
  if (!heatmapTooltip) return;
  
  const fullSignal = pageState.flatSignals[signal.id];
  const historyData = pageState.history[signal.id] || [];
  const signalDoc = getSignalDoc(signal.id);
  
  // Header - Abbreviation
  const abbrEl = heatmapTooltip.querySelector('.heatmap-tooltip__abbr');
  if (abbrEl) abbrEl.textContent = signal.abbr;
  
  // Header - Tier badge
  const tierEl = heatmapTooltip.querySelector('.heatmap-tooltip__tier');
  if (tierEl) {
    if (signalDoc?.tier) {
      tierEl.textContent = getTierLabel(signalDoc.tier);
      tierEl.className = `heatmap-tooltip__tier heatmap-tooltip__tier--${signalDoc.tier}`;
      tierEl.style.display = 'inline-block';
    } else {
      tierEl.style.display = 'none';
    }
  }
  
  // Header - Status badge
  const statusEl = heatmapTooltip.querySelector('.heatmap-tooltip__status');
  if (statusEl) {
    statusEl.textContent = signal.level.toUpperCase();
    statusEl.className = `heatmap-tooltip__status heatmap-tooltip__status--${signal.level}`;
  }
  
  // Name - use shortName from docs if available
  const nameEl = heatmapTooltip.querySelector('.heatmap-tooltip__name');
  if (nameEl) {
    nameEl.textContent = signalDoc?.shortName || signal.name;
  }
  
  // Description from documentation
  const descEl = heatmapTooltip.querySelector('.heatmap-tooltip__description');
  if (descEl) {
    if (signalDoc?.description) {
      descEl.textContent = truncateText(signalDoc.description, 150);
      descEl.style.display = 'block';
    } else {
      descEl.style.display = 'none';
    }
  }
  
  // Value
  const valueEl = heatmapTooltip.querySelector('.heatmap-tooltip__value');
  if (valueEl) {
    valueEl.textContent = signal.hasData 
      ? formatHeatmapValue(signal.value, signal.id) 
      : 'No Data';
  }
  
  // Change
  const changeEl = heatmapTooltip.querySelector('.heatmap-tooltip__change');
  if (changeEl) {
    const change = calculateChange(historyData);
    changeEl.innerHTML = formatChange(change);
  }
  
  // Sparkline
  const sparklineEl = heatmapTooltip.querySelector('.heatmap-tooltip__sparkline');
  if (sparklineEl) {
    if (!signal.hasData) {
      sparklineEl.innerHTML = '<span class="sparkline-empty">No data</span>';
    } else {
      renderTooltipSparkline(sparklineEl, historyData, signal.level);
    }
  }
  
  // Interpretation based on current level
  const interpEl = heatmapTooltip.querySelector('.heatmap-tooltip__interpretation');
  if (interpEl) {
    const interpText = getInterpretationForLevel(signalDoc, signal.level);
    if (interpText) {
      interpEl.innerHTML = `
        <span class="heatmap-tooltip__interpretation-label">Current Read:</span>
        <span class="heatmap-tooltip__interpretation-text">${truncateText(interpText, 120)}</span>
      `;
      interpEl.style.display = 'block';
    } else {
      interpEl.style.display = 'none';
    }
  }
  
  // Key levels context
  const keyLevelsEl = heatmapTooltip.querySelector('.heatmap-tooltip__key-levels');
  if (keyLevelsEl) {
    if (signalDoc?.keyLevels && signalDoc.keyLevels.length > 0 && signal.hasData) {
      // Find the closest key level to current value
      const keyLevelHtml = signalDoc.keyLevels.slice(0, 2).map(kl => 
        `<span class="heatmap-tooltip__key-level">
          <span class="key-level__value">${kl.level}</span>
          <span class="key-level__desc">${truncateText(kl.significance, 60)}</span>
        </span>`
      ).join('');
      keyLevelsEl.innerHTML = `
        <span class="heatmap-tooltip__key-levels-label">Key Levels:</span>
        <span class="heatmap-tooltip__key-levels-list">${keyLevelHtml}</span>
      `;
      keyLevelsEl.style.display = 'block';
    } else {
      keyLevelsEl.style.display = 'none';
    }
  }
  
  // Context - Percentile
  const contextEl = heatmapTooltip.querySelector('.heatmap-tooltip__context');
  if (contextEl) {
    const percentile = calculatePercentile(signal.value, historyData);
    contextEl.innerHTML = renderPercentileHTML(percentile);
  }
  
  // Formula - for CAP computed signals (prefer doc calculation)
  const formulaEl = heatmapTooltip.querySelector('.heatmap-tooltip__formula');
  if (formulaEl) {
    if (signalDoc?.calculation?.formula) {
      formulaEl.innerHTML = `
        <span class="heatmap-tooltip__formula-label">Formula:</span>
        <span class="heatmap-tooltip__formula-value">${signalDoc.calculation.formula}</span>
      `;
      formulaEl.style.display = 'flex';
    } else if (signal.isCap) {
      const formulaInfo = getSignalFormula(signal.id);
      if (formulaInfo && formulaInfo.showFormula) {
        formulaEl.innerHTML = `
          <span class="heatmap-tooltip__formula-label">Derived:</span>
          <span class="heatmap-tooltip__formula-value">${formulaInfo.formula}</span>
        `;
        formulaEl.style.display = 'flex';
      } else {
        formulaEl.innerHTML = '';
        formulaEl.style.display = 'none';
      }
    } else {
      formulaEl.innerHTML = '';
      formulaEl.style.display = 'none';
    }
  }
}

/**
 * Positions the tooltip relative to the tile
 * @param {HTMLElement} tileEl - The hovered tile element
 */
function positionTooltip(tileEl) {
  if (!heatmapTooltip) return;
  
  const tileRect = tileEl.getBoundingClientRect();
  const tooltipRect = heatmapTooltip.getBoundingClientRect();
  
  const OFFSET = 12; // gap between tile and tooltip
  const PADDING = 16; // minimum distance from viewport edge
  
  let top, left;
  let position = 'above';
  
  // Default: position above the tile
  top = tileRect.top - tooltipRect.height - OFFSET;
  
  // If would clip top, position below
  if (top < PADDING) {
    top = tileRect.bottom + OFFSET;
    position = 'below';
  }
  
  // Horizontal: center on tile
  left = tileRect.left + (tileRect.width / 2) - (tooltipRect.width / 2);
  
  // Clamp to viewport
  left = Math.max(PADDING, Math.min(left, window.innerWidth - tooltipRect.width - PADDING));
  
  // Ensure tooltip doesn't go off bottom of screen
  if (position === 'below' && top + tooltipRect.height > window.innerHeight - PADDING) {
    top = window.innerHeight - tooltipRect.height - PADDING;
  }
  
  heatmapTooltip.style.top = `${top}px`;
  heatmapTooltip.style.left = `${left}px`;
  
  // Update arrow direction
  heatmapTooltip.classList.remove('heatmap-tooltip--above', 'heatmap-tooltip--below');
  heatmapTooltip.classList.add(`heatmap-tooltip--${position}`);
}

/**
 * Shows the tooltip for a signal tile
 * @param {Object} signal - Heatmap signal data
 * @param {HTMLElement} tileEl - The tile element
 */
function showHeatmapTooltip(signal, tileEl) {
  clearTimeout(tooltipTimeout);
  
  tooltipTimeout = setTimeout(() => {
    if (!heatmapTooltip) return;
    
    // Mark the tile as active
    document.querySelectorAll('.heatmap-tile--tooltip-active').forEach(el => {
      el.classList.remove('heatmap-tile--tooltip-active');
    });
    tileEl.classList.add('heatmap-tile--tooltip-active');
    
    updateTooltipContent(signal);
    heatmapTooltip.classList.add('heatmap-tooltip--visible');
    
    // Position after visible for accurate measurements
    requestAnimationFrame(() => {
      positionTooltip(tileEl);
    });
  }, TOOLTIP_DELAY);
}

/**
 * Hides the heatmap tooltip
 */
function hideHeatmapTooltip() {
  clearTimeout(tooltipTimeout);
  
  if (heatmapTooltip) {
    heatmapTooltip.classList.remove('heatmap-tooltip--visible');
  }
  
  document.querySelectorAll('.heatmap-tile--tooltip-active').forEach(el => {
    el.classList.remove('heatmap-tile--tooltip-active');
  });
}

// ============================================================================
// SIGNAL CARD TOOLTIP SYSTEM (Phase 5)
// ============================================================================

/**
 * Creates the signal card tooltip DOM structure
 * Compact hover tooltip with tier, interpretation, and CTA
 * @returns {HTMLElement}
 */
function createCardTooltip() {
  return el('div', { className: 'card-tooltip', 'data-component': 'card-tooltip' }, [
    el('div', { className: 'card-tooltip__header' }, [
      el('span', { className: 'card-tooltip__name' }),
      el('span', { className: 'card-tooltip__tier' }),
    ]),
    el('div', { className: 'card-tooltip__description' }),
    el('div', { className: 'card-tooltip__interpretation' }),
    el('div', { className: 'card-tooltip__cta' }, ['Click for full details →']),
  ]);
}

/**
 * Ensures card tooltip exists in DOM
 */
function ensureCardTooltip() {
  if (!cardTooltip) {
    cardTooltip = createCardTooltip();
    document.body.appendChild(cardTooltip);
  }
}

/**
 * Updates card tooltip content
 * @param {string} signalId - Signal ID
 * @param {Object} signal - Signal data
 */
function updateCardTooltipContent(signalId, signal) {
  if (!cardTooltip) return;
  
  const signalDoc = getSignalDoc(signalId);
  const meta = SIGNAL_METADATA[signalId] || {};
  const currentLevel = signal?.current?.level;
  
  // Name
  const nameEl = cardTooltip.querySelector('.card-tooltip__name');
  if (nameEl) {
    nameEl.textContent = signalDoc?.shortName || signalDoc?.name || meta.name || signalId;
  }
  
  // Tier badge
  const tierEl = cardTooltip.querySelector('.card-tooltip__tier');
  if (tierEl) {
    if (signalDoc?.tier) {
      tierEl.textContent = getTierLabel(signalDoc.tier);
      tierEl.className = `card-tooltip__tier card-tooltip__tier--${signalDoc.tier}`;
      tierEl.style.display = 'inline-block';
    } else {
      tierEl.style.display = 'none';
    }
  }
  
  // Description
  const descEl = cardTooltip.querySelector('.card-tooltip__description');
  if (descEl) {
    if (signalDoc?.description) {
      descEl.textContent = truncateText(signalDoc.description, 100);
      descEl.style.display = 'block';
    } else if (meta.description) {
      descEl.textContent = truncateText(meta.description, 100);
      descEl.style.display = 'block';
    } else {
      descEl.style.display = 'none';
    }
  }
  
  // Interpretation based on current level
  const interpEl = cardTooltip.querySelector('.card-tooltip__interpretation');
  if (interpEl) {
    const interpText = getInterpretationForLevel(signalDoc, currentLevel);
    if (interpText) {
      interpEl.innerHTML = `
        <span class="card-tooltip__interpretation-label">Current:</span>
        <span class="card-tooltip__interpretation-text">${truncateText(interpText, 80)}</span>
      `;
      interpEl.style.display = 'block';
    } else {
      interpEl.style.display = 'none';
    }
  }
}

/**
 * Positions the card tooltip relative to the card
 * @param {HTMLElement} cardEl - The signal card element
 */
function positionCardTooltip(cardEl) {
  if (!cardTooltip) return;
  
  const cardRect = cardEl.getBoundingClientRect();
  const tooltipRect = cardTooltip.getBoundingClientRect();
  
  const OFFSET = 8;
  const PADDING = 16;
  
  let top, left;
  let position = 'above';
  
  // Default: position above the card
  top = cardRect.top - tooltipRect.height - OFFSET;
  
  // If would clip top, position below
  if (top < PADDING) {
    top = cardRect.bottom + OFFSET;
    position = 'below';
  }
  
  // Horizontal: center on card, but clamp to viewport
  left = cardRect.left + (cardRect.width / 2) - (tooltipRect.width / 2);
  left = Math.max(PADDING, Math.min(left, window.innerWidth - tooltipRect.width - PADDING));
  
  // Ensure doesn't go off bottom
  if (position === 'below' && top + tooltipRect.height > window.innerHeight - PADDING) {
    top = window.innerHeight - tooltipRect.height - PADDING;
  }
  
  cardTooltip.style.top = `${top}px`;
  cardTooltip.style.left = `${left}px`;
  
  cardTooltip.classList.remove('card-tooltip--above', 'card-tooltip--below');
  cardTooltip.classList.add(`card-tooltip--${position}`);
}

/**
 * Shows the card tooltip for a signal card
 * @param {string} signalId - Signal ID
 * @param {Object} signal - Signal data
 * @param {HTMLElement} cardEl - The card element
 */
function showCardTooltip(signalId, signal, cardEl) {
  clearTimeout(cardTooltipTimeout);
  
  cardTooltipTimeout = setTimeout(() => {
    ensureCardTooltip();
    
    // Mark card as active
    document.querySelectorAll('.signal-card--tooltip-active').forEach(el => {
      el.classList.remove('signal-card--tooltip-active');
    });
    cardEl.classList.add('signal-card--tooltip-active');
    
    updateCardTooltipContent(signalId, signal);
    cardTooltip.classList.add('card-tooltip--visible');
    
    requestAnimationFrame(() => {
      positionCardTooltip(cardEl);
    });
  }, CARD_TOOLTIP_DELAY);
}

/**
 * Hides the card tooltip
 */
function hideCardTooltip() {
  clearTimeout(cardTooltipTimeout);
  
  if (cardTooltip) {
    cardTooltip.classList.remove('card-tooltip--visible');
  }
  
  document.querySelectorAll('.signal-card--tooltip-active').forEach(el => {
    el.classList.remove('signal-card--tooltip-active');
  });
}

/**
 * Attaches tooltip handlers to a signal card
 * @param {HTMLElement} cardEl - The card element
 * @param {string} signalId - Signal ID
 * @param {Object} signal - Signal data
 */
function attachCardTooltipHandlers(cardEl, signalId, signal) {
  cardEl.addEventListener('mouseenter', () => showCardTooltip(signalId, signal, cardEl));
  cardEl.addEventListener('mouseleave', hideCardTooltip);
  cardEl.addEventListener('focus', () => showCardTooltip(signalId, signal, cardEl));
  cardEl.addEventListener('blur', hideCardTooltip);
}

// ============================================================================
// STRESS CALCULATIONS
// ============================================================================

/**
 * Calculates stress index from signal data
 */
function calculateStressIndex(signals) {
  const weights = {
    NET_LIQUIDITY: 0.15,
    CURVE_2S10S: 0.10,
    HY_SPREAD: 0.15,
    CREDIT_STRESS_INDEX: 0.15,
    FUNDING_STRESS_INDEX: 0.15,
    VIX_LEVEL: 0.10,
    VIX_ZSCORE: 0.10,
    CAP_STRESS_INDEX: 0.10,
  };
  
  let totalScore = 0;
  let totalWeight = 0;
  
  Object.entries(weights).forEach(([signalId, weight]) => {
    const signal = signals[signalId];
    if (signal?.current?.level) {
      totalWeight += weight;
      const levelScore = { low: 10, normal: 25, calm: 20, elevated: 50, high: 75, extreme: 95, stressed: 80, crisis: 95 };
      totalScore += (levelScore[signal.current.level] || 25) * weight;
    }
  });
  
  const value = totalWeight > 0 ? totalScore / totalWeight : 30;
  const status = getGaugeStatus(value, 'stress');
  
  return { value: Math.round(value), status };
}

/**
 * Mock 7-day historical data for CAP Stress Index sparkline
 * In production, this would come from the API
 */
function getMockStressHistory(currentValue) {
  const today = new Date();
  const variance = 8; // Max daily change
  const history = [];
  
  // Generate 7 days of history working backward from current value
  let value = currentValue;
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    if (i === 0) {
      // Today's value is the current value
      history.push({ date: date.toISOString().split('T')[0], value: currentValue });
    } else {
      // Random walk backward with some trend
      value = Math.max(10, Math.min(90, value + (Math.random() - 0.5) * variance));
      history.push({ date: date.toISOString().split('T')[0], value: Math.round(value) });
    }
  }
  
  return history;
}

/**
 * Renders a mini sparkline for the stress gauge
 * @param {HTMLElement} container - Container element
 * @param {Array} historyData - 7-day history [{date, value}]
 * @param {number} currentValue - Current stress value
 */
function renderStressGaugeSparkline(container, historyData, currentValue) {
  if (!historyData || historyData.length < 2) {
    container.innerHTML = '<span class="stress-sparkline-empty">No trend data</span>';
    return;
  }
  
  const width = 80;
  const height = 28;
  const padding = { top: 2, right: 2, bottom: 2, left: 2 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Calculate scales
  const values = historyData.map(d => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  
  // Generate points
  const points = historyData.map((d, i) => {
    const x = padding.left + (i / (historyData.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - ((d.value - minVal) / range) * chartHeight;
    return { x, y };
  });
  
  // Build SVG path
  const pathD = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
  ).join(' ');
  
  // Determine trend direction for color
  const firstValue = historyData[0].value;
  const lastValue = historyData[historyData.length - 1].value;
  const trendUp = lastValue > firstValue;
  const trendColor = trendUp ? 'var(--tg-rust-red)' : 'var(--tg-fog-green)'; // Red if stress increasing, green if decreasing
  
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'stress-gauge-sparkline');
  svg.setAttribute('width', width);
  svg.setAttribute('height', height);
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  
  svg.innerHTML = `
    <path d="${pathD}" fill="none" stroke="${trendColor}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.9"/>
    <circle cx="${points[points.length - 1].x.toFixed(1)}" cy="${points[points.length - 1].y.toFixed(1)}" r="2" fill="${trendColor}"/>
  `;
  
  container.innerHTML = '';
  container.appendChild(svg);
}

/**
 * Creates the CAP Stress Index War Room Gauge
 * Dramatic centerpiece display with component breakdown
 */
function createStressWarRoom(stressData = {}) {
  const { value = 30, status = 'normal', components = {} } = stressData;
  
  // Default component scores if not provided
  const defaultComponents = {
    'Liquidity': components.liquidity || 30,
    'Rates': components.rates || 35,
    'Credit': components.credit || 40,
    'Volatility': components.volatility || 25,
    'Macro': components.macro || 35,
  };
  
  const markerPosition = Math.min(100, Math.max(0, value));
  
  // Generate mock 7-day history for sparkline
  const stressHistory = getMockStressHistory(value);
  
  // Helper to create component score bars
  const createComponentBar = (name, score) => {
    const percentage = Math.min(100, Math.max(0, score));
    const level = score > 75 ? 'high' : score > 50 ? 'elevated' : score > 25 ? 'normal' : 'calm';
    
    return el('div', { className: 'stress-gauge__component' }, [
      el('div', { className: 'stress-gauge__component-header' }, [
        el('span', { className: 'stress-gauge__component-name' }, [name]),
        el('span', { className: `stress-gauge__component-value stress-gauge__component-value--${level}` }, [String(Math.round(score))]),
      ]),
      el('div', { className: 'stress-gauge__component-bar' }, [
        el('div', { 
          className: `stress-gauge__component-fill stress-gauge__component-fill--${level}`,
          style: { width: `${percentage}%` }
        }),
      ]),
    ]);
  };
  
  // Create the sparkline container
  const sparklineContainer = el('div', { className: 'stress-gauge__sparkline-wrap' }, [
    el('div', { className: 'stress-gauge__sparkline-label' }, ['7D TREND']),
    el('div', { className: 'stress-gauge__sparkline', 'data-sparkline': 'stress-gauge' }),
  ]);
  
  const gauge = el('div', { className: 'stress-gauge', 'data-gauge-id': 'stress-war-room' }, [
    // Header
    el('div', { className: 'stress-gauge__header' }, [
      el('h2', { className: 'stress-gauge__title' }, ['CAP Stress Index']),
      el('span', { className: `stress-gauge__badge stress-gauge__badge--${status}` }, [status.toUpperCase()]),
    ]),
    
    // Big Score with Sparkline
    el('div', { className: 'stress-gauge__score-row' }, [
      el('div', { className: 'stress-gauge__score' }, [
        el('span', { className: 'stress-gauge__value' }, [String(value)]),
        el('span', { className: 'stress-gauge__max' }, ['/ 100']),
      ]),
      sparklineContainer,
    ]),
    
    // Level Label
    el('div', { className: `stress-gauge__level stress-gauge__level--${status}` }, [
      status.toUpperCase()
    ]),
    
    // Gauge Bar
    el('div', { className: 'stress-gauge__bar-container' }, [
      el('div', { className: 'stress-gauge__bar' }, [
        el('div', { 
          className: 'stress-gauge__marker',
          style: { left: `${markerPosition}%` }
        }),
      ]),
      el('div', { className: 'stress-gauge__labels' }, [
        el('span', {}, ['CALM']),
        el('span', {}, ['NORMAL']),
        el('span', {}, ['ELEVATED']),
        el('span', {}, ['HIGH']),
        el('span', {}, ['CRISIS']),
      ]),
    ]),
    
    // Component breakdown
    el('div', { className: 'stress-gauge__components' }, [
      el('h3', { className: 'stress-gauge__components-title' }, ['Component Breakdown']),
      el('div', { className: 'stress-gauge__components-grid' }, 
        Object.entries(defaultComponents).map(([name, score]) => createComponentBar(name, score))
      ),
    ]),
  ]);
  
  // Render sparkline after element is created
  requestAnimationFrame(() => {
    const sparklineEl = gauge.querySelector('[data-sparkline="stress-gauge"]');
    if (sparklineEl) {
      renderStressGaugeSparkline(sparklineEl, stressHistory, value);
    }
  });
  
  return gauge;
}

/**
 * Updates the stress war room gauge
 */
function updateStressWarRoom(stressData) {
  const gauge = document.querySelector('[data-gauge-id="stress-war-room"]');
  if (!gauge) return;
  
  const { value = 30, status = 'normal', components = {} } = stressData;
  const markerPosition = Math.min(100, Math.max(0, value));
  
  // Update score
  const valueEl = gauge.querySelector('.stress-gauge__value');
  if (valueEl) valueEl.textContent = String(value);
  
  // Update sparkline with fresh mock data
  const sparklineEl = gauge.querySelector('[data-sparkline="stress-gauge"]');
  if (sparklineEl) {
    const stressHistory = getMockStressHistory(value);
    renderStressGaugeSparkline(sparklineEl, stressHistory, value);
  }
  
  // Update badge
  const badgeEl = gauge.querySelector('.stress-gauge__badge');
  if (badgeEl) {
    badgeEl.textContent = status.toUpperCase();
    badgeEl.className = `stress-gauge__badge stress-gauge__badge--${status}`;
  }
  
  // Update level label
  const levelEl = gauge.querySelector('.stress-gauge__level');
  if (levelEl) {
    levelEl.textContent = status.toUpperCase();
    levelEl.className = `stress-gauge__level stress-gauge__level--${status}`;
  }
  
  // Update marker position
  const markerEl = gauge.querySelector('.stress-gauge__marker');
  if (markerEl) markerEl.style.left = `${markerPosition}%`;
  
  // Update component bars
  const componentBars = gauge.querySelectorAll('.stress-gauge__component');
  const componentData = {
    'Liquidity': components.liquidity || 30,
    'Rates': components.rates || 35,
    'Credit': components.credit || 40,
    'Volatility': components.volatility || 25,
    'Macro': components.macro || 35,
  };
  
  const componentNames = Object.keys(componentData);
  componentBars.forEach((bar, i) => {
    const name = componentNames[i];
    const score = componentData[name] || 30;
    const level = score > 75 ? 'high' : score > 50 ? 'elevated' : score > 25 ? 'normal' : 'calm';
    
    const valueEl = bar.querySelector('.stress-gauge__component-value');
    if (valueEl) {
      valueEl.textContent = String(Math.round(score));
      valueEl.className = `stress-gauge__component-value stress-gauge__component-value--${level}`;
    }
    
    const fillEl = bar.querySelector('.stress-gauge__component-fill');
    if (fillEl) {
      fillEl.style.width = `${Math.min(100, Math.max(0, score))}%`;
      fillEl.className = `stress-gauge__component-fill stress-gauge__component-fill--${level}`;
    }
  });
}

// ============================================================================
// CSI HISTORY CHART COMPONENT
// ============================================================================

/**
 * Generates mock CSI history data for demonstration
 * In production, this would come from the API with real historical CSI values
 * @param {number} currentScore - Current CSI score
 * @param {number} days - Number of days of history to generate
 * @returns {Array<{date: string, score: number, regime: string}>}
 */
function generateMockCSIHistory(currentScore, days = 90) {
  const history = [];
  const today = new Date();
  let score = currentScore;
  
  // Generate history working backward from current value
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Add some realistic variance (mean reverting random walk)
    if (i > 0) {
      const meanReversion = (45 - score) * 0.05; // Pull toward 45
      const noise = (Math.random() - 0.5) * 8;
      score = Math.max(15, Math.min(85, score + meanReversion + noise));
    } else {
      score = currentScore; // Today is current
    }
    
    // Determine regime based on score
    let regime = 'NORMAL';
    if (score >= 75) regime = 'CRISIS';
    else if (score >= 60) regime = 'HIGH';
    else if (score >= 45) regime = 'ELEVATED';
    else if (score < 30) regime = 'CALM';
    
    history.push({
      date: date.toISOString().split('T')[0],
      score: Math.round(score * 10) / 10,
      regime
    });
  }
  
  return history;
}

/**
 * Creates the CSI History Chart component
 * Clean, elegant 90-day sparkline matching signal card aesthetic
 * @param {Object} options
 * @param {number} options.currentScore - Current CSI score
 * @param {string} options.currentRegime - Current regime (CALM, NORMAL, ELEVATED, HIGH, CRISIS)
 * @param {Array} [options.history] - Historical data (uses mock if not provided)
 * @returns {{element: HTMLElement, update: Function, destroy: Function}}
 */
function createCSIHistoryChart(options = {}) {
  const { currentScore = 45, currentRegime = 'NORMAL', history } = options;
  
  // Use provided history or generate mock data
  let chartHistory = history || generateMockCSIHistory(currentScore);
  
  // Create clean sparkline SVG (matches signal card style)
  function renderSparkline(data, score) {
    if (!data || data.length < 2) return '<span class="csi-history__empty">No data</span>';
    
    const width = 280;
    const height = 60;
    const padding = { top: 4, right: 4, bottom: 4, left: 4 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    // Get value range with some padding
    const scores = data.map(d => d.score);
    const minVal = Math.min(...scores);
    const maxVal = Math.max(...scores);
    const range = maxVal - minVal || 1;
    const yPadding = range * 0.1;
    const yMin = minVal - yPadding;
    const yMax = maxVal + yPadding;
    const yRange = yMax - yMin;
    
    // Generate points
    const points = data.map((d, i) => ({
      x: padding.left + (i / (data.length - 1)) * chartWidth,
      y: padding.top + chartHeight - ((d.score - yMin) / yRange) * chartHeight
    }));
    
    // Build path
    const pathD = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
    ).join(' ');
    
    // Area fill path
    const areaD = pathD + 
      ` L ${points[points.length - 1].x.toFixed(1)} ${height - padding.bottom}` +
      ` L ${points[0].x.toFixed(1)} ${height - padding.bottom} Z`;
    
    // Color based on current score
    const lineColor = score >= 60 ? '#B2332A' : score >= 45 ? '#f97316' : '#00EEE5';
    const lastPoint = points[points.length - 1];
    
    return `
      <svg width="100%" height="${height}" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" class="csi-history__sparkline">
        <path d="${areaD}" fill="${lineColor}" fill-opacity="0.12" />
        <path d="${pathD}" fill="none" stroke="${lineColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="${lastPoint.x.toFixed(1)}" cy="${lastPoint.y.toFixed(1)}" r="3.5" fill="${lineColor}" />
      </svg>
    `;
  }
  
  // Get color class for current score
  function getColorClass(score) {
    if (score >= 60) return 'stressed';
    if (score >= 45) return 'elevated';
    if (score < 30) return 'calm';
    return 'normal';
  }
  
  // Get regime label
  function getRegimeLabel(score) {
    if (score >= 75) return 'CRISIS';
    if (score >= 60) return 'HIGH';
    if (score >= 45) return 'ELEVATED';
    if (score < 30) return 'CALM';
    return 'NORMAL';
  }
  
  // Calculate trend
  function getTrend(data) {
    if (!data || data.length < 7) return null;
    const recent = data.slice(-7);
    const first = recent[0].score;
    const last = recent[recent.length - 1].score;
    const change = last - first;
    const pctChange = ((change / first) * 100).toFixed(1);
    return {
      direction: change > 0.5 ? 'up' : change < -0.5 ? 'down' : 'flat',
      change: Math.abs(change).toFixed(1),
      pctChange: Math.abs(parseFloat(pctChange)).toFixed(1),
      sign: change >= 0 ? '+' : '-'
    };
  }
  
  const colorClass = getColorClass(currentScore);
  const trend = getTrend(chartHistory);
  
  // Create the component element (styled like Recession Probability widget)
  const element = el('div', { className: `csi-history-chart csi-history-chart--${colorClass}`, 'data-component': 'csi-history' }, [
    el('div', { className: 'csi-history__header' }, [
      el('h2', { className: 'csi-history__title' }, ['Stress History']),
      el('span', { className: `csi-history__badge csi-history__badge--${colorClass}` }, [getRegimeLabel(currentScore)]),
    ]),
    el('div', { className: 'csi-history__score-section' }, [
      el('div', { className: 'csi-history__value-row' }, [
        el('span', { className: 'csi-history__score' }, [String(Math.round(currentScore))]),
        el('span', { className: 'csi-history__max' }, ['/ 100']),
        trend ? el('span', { className: `csi-history__trend csi-history__trend--${trend.direction}` }, [
          trend.direction === 'up' ? '▲' : trend.direction === 'down' ? '▼' : '→',
          ` ${trend.sign}${trend.change}`,
        ]) : null,
      ].filter(Boolean)),
      el('p', { className: 'csi-history__description' }, [
        '90-day composite stress index tracking liquidity, credit, and volatility conditions.'
      ]),
    ]),
    el('div', { className: 'csi-history__chart' }),
    el('div', { className: 'csi-history__meta' }, [
      el('span', { className: 'csi-history__period' }, ['90 Day Trend']),
      el('span', { className: 'csi-history__range-hint' }, ['0-100 Scale']),
    ]),
  ]);
  
  // Render initial chart
  const chartContainer = element.querySelector('.csi-history__chart');
  if (chartContainer) {
    chartContainer.innerHTML = renderSparkline(chartHistory, currentScore);
  }
  
  return {
    element,
    
    update(newOptions) {
      const { currentScore: newScore, history: newHistory } = newOptions;
      const score = newScore ?? currentScore;
      
      chartHistory = newHistory || generateMockCSIHistory(score);
      const newColorClass = getColorClass(score);
      const newTrend = getTrend(chartHistory);
      
      // Update class
      element.className = `csi-history-chart csi-history-chart--${newColorClass}`;
      element.setAttribute('data-component', 'csi-history');
      
      // Update badge
      const badge = element.querySelector('.csi-history__badge');
      if (badge) {
        badge.textContent = getRegimeLabel(score);
        badge.className = `csi-history__badge csi-history__badge--${newColorClass}`;
      }
      
      // Update score
      const scoreEl = element.querySelector('.csi-history__score');
      if (scoreEl) scoreEl.textContent = String(Math.round(score));
      
      // Update trend
      let trendEl = element.querySelector('.csi-history__trend');
      const valueRow = element.querySelector('.csi-history__value-row');
      
      if (newTrend) {
        if (!trendEl && valueRow) {
          // Create trend element if it doesn't exist
          trendEl = document.createElement('span');
          valueRow.appendChild(trendEl);
        }
        if (trendEl) {
          trendEl.className = `csi-history__trend csi-history__trend--${newTrend.direction}`;
          trendEl.textContent = `${newTrend.direction === 'up' ? '▲' : newTrend.direction === 'down' ? '▼' : '→'} ${newTrend.sign}${newTrend.change}`;
        }
      } else if (trendEl) {
        trendEl.remove();
      }
      
      // Re-render chart
      const container = element.querySelector('.csi-history__chart');
      if (container) {
        container.innerHTML = renderSparkline(chartHistory, score);
      }
    },
    
    destroy() {
      element.remove();
    }
  };
}

// ============================================================================
// DATA FRESHNESS WIDGET
// ============================================================================

/**
 * Creates the Data Freshness widget for the corner
 * Shows last update time, data quality, and refresh button
 * @returns {{element: HTMLElement, update: Function, destroy: Function}}
 */
function createDataFreshnessWidget() {
  const now = new Date();
  
  function formatTime(date) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  
  function formatDate(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  
  const element = el('div', { className: 'data-freshness', 'data-component': 'data-freshness' }, [
    el('div', { className: 'data-freshness__header' }, [
      el('span', { className: 'data-freshness__icon' }, ['📡']),
      el('span', { className: 'data-freshness__label' }, ['Data Status']),
      el('div', { className: 'data-freshness__status data-freshness__status--live' }, [
        el('span', { className: 'data-freshness__status-dot' }),
        el('span', {}, ['Live']),
      ]),
    ]),
    el('div', { className: 'data-freshness__grid' }, [
      el('div', { className: 'data-freshness__item' }, [
        el('span', { className: 'data-freshness__item-label' }, ['Last Update']),
        el('span', { className: 'data-freshness__item-value', 'data-field': 'time' }, [formatTime(now)]),
      ]),
      el('div', { className: 'data-freshness__item' }, [
        el('span', { className: 'data-freshness__item-label' }, ['Date']),
        el('span', { className: 'data-freshness__item-value', 'data-field': 'date' }, [formatDate(now)]),
      ]),
      el('div', { className: 'data-freshness__item' }, [
        el('span', { className: 'data-freshness__item-label' }, ['Signals']),
        el('span', { className: 'data-freshness__item-value', 'data-field': 'signals' }, ['0 / 120']),
      ]),
      el('div', { className: 'data-freshness__item' }, [
        el('span', { className: 'data-freshness__item-label' }, ['Quality']),
        el('span', { className: 'data-freshness__item-value', 'data-field': 'quality' }, ['—']),
      ]),
    ]),
    el('div', { className: 'data-freshness__footer' }, [
      el('span', { className: 'data-freshness__next-update' }, ['Auto-refresh: 5m']),
      el('button', { 
        className: 'data-freshness__refresh-btn',
        onclick: () => loadSignalData()
      }, ['↻ Refresh']),
    ]),
  ]);
  
  return {
    element,
    
    update(data = {}) {
      const { loaded = 0, total = 120, lastUpdated } = data;
      const updateTime = lastUpdated || new Date();
      const quality = loaded / total;
      
      // Update time
      const timeEl = element.querySelector('[data-field="time"]');
      if (timeEl) timeEl.textContent = formatTime(updateTime);
      
      // Update date
      const dateEl = element.querySelector('[data-field="date"]');
      if (dateEl) dateEl.textContent = formatDate(updateTime);
      
      // Update signals count
      const signalsEl = element.querySelector('[data-field="signals"]');
      if (signalsEl) signalsEl.textContent = `${loaded} / ${total}`;
      
      // Update quality
      const qualityEl = element.querySelector('[data-field="quality"]');
      if (qualityEl) {
        const qualityPct = Math.round(quality * 100);
        qualityEl.textContent = `${qualityPct}%`;
        qualityEl.classList.toggle('data-freshness__item-value--warning', qualityPct < 80);
      }
      
      // Update status indicator
      const statusEl = element.querySelector('.data-freshness__status');
      if (statusEl) {
        const isLive = quality > 0.5;
        statusEl.className = `data-freshness__status data-freshness__status--${isLive ? 'live' : 'stale'}`;
        statusEl.querySelector('span:last-child').textContent = isLive ? 'Live' : 'Stale';
      }
    },
    
    destroy() {
      element.remove();
    }
  };
}

/** @type {{element: HTMLElement, update: Function, destroy: Function}|null} */
let dataFreshnessInstance = null;

/**
 * Generates a CSI result object from current signal data.
 * This creates a mock CSI result based on the loaded signals.
 * In production, this would come from the CSI engine with full history.
 * @param {Object} signals - Flat signals object
 * @returns {import('../csi/types.js').CSIResult}
 */
function generateCSIFromSignals(signals) {
  /**
   * Helper to get stress score from signal level with improved precision.
   * Uses percentile-based scoring when history is available, otherwise
   * derives a meaningful score from the signal's value characteristics.
   * @param {string} signalId
   * @returns {{score: number, hasData: boolean}}
   */
  const getScoreWithMeta = (signalId) => {
    const signal = signals[signalId];
    const historyData = pageState.history[signalId];
    
    // No signal data at all
    if (!signal?.current) {
      return { score: null, hasData: false };
    }
    
    const level = signal.current.level;
    const value = signal.current.value;
    
    // Base scores by level (with ranges for variance)
    const levelScoreRanges = {
      low: [15, 25],
      calm: [20, 30],
      tight: [25, 35],
      normal: [40, 55],
      flat: [45, 55],
      elevated: [55, 70],
      wide: [55, 70],
      high: [65, 80],
      stressed: [70, 85],
      inverted: [75, 85],
      extreme: [80, 95],
      crisis: [85, 98]
    };
    
    // If we have history, calculate percentile for more precise scoring
    if (historyData && historyData.length >= 10 && value != null) {
      const percentile = calculatePercentile(value, historyData);
      if (percentile !== null) {
        // Map percentile to stress score (higher percentile = higher stress for most signals)
        // Blend percentile with level-based score for robustness
        const range = levelScoreRanges[level] || [35, 60];
        const baseScore = (range[0] + range[1]) / 2;
        const percentileScore = percentile * 0.9 + 5; // Scale 0-100 to 5-95
        
        // Weight: 60% percentile, 40% level-based
        const blendedScore = percentileScore * 0.6 + baseScore * 0.4;
        return { score: Math.round(blendedScore * 10) / 10, hasData: true };
      }
    }
    
    // Fallback: use level with variance based on signal ID hash
    if (level && levelScoreRanges[level]) {
      const [min, max] = levelScoreRanges[level];
      // Create deterministic variance based on signal ID
      const hash = signalId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const variance = (hash % 100) / 100; // 0 to 1
      const score = min + variance * (max - min);
      return { score: Math.round(score * 10) / 10, hasData: true };
    }
    
    // Unknown level but has value - estimate from value characteristics
    if (value != null) {
      // Use a hash of the value to create variance
      const valueHash = Math.abs(Math.sin(value * 12.9898)) * 43758.5453;
      const normalizedHash = valueHash - Math.floor(valueHash);
      const score = 35 + normalizedHash * 25; // Range 35-60
      return { score: Math.round(score * 10) / 10, hasData: true };
    }
    
    // No usable data
    return { score: null, hasData: false };
  };

  /**
   * Legacy helper for simple score lookup (for drivers table)
   * @param {string} signalId
   * @returns {number}
   */
  const getScore = (signalId) => {
    const result = getScoreWithMeta(signalId);
    return result.score ?? 45; // Default to neutral-ish if no data
  };

  // Calculate pillar scores from representative signals
  const pillarScores = {
    FUNDING: Math.round((getScore('SOFR') + getScore('EFFR') + getScore('OBFR')) / 3),
    LIQUIDITY: Math.round((getScore('NET_LIQUIDITY') + getScore('WALCL') + getScore('WTREGEN') + getScore('RRPONTSYD')) / 4),
    RATES_CURVE: Math.round((getScore('DGS10') + getScore('DGS2') + getScore('CURVE_2S10S') + getScore('CURVE_3M10Y')) / 4),
    CREDIT: Math.round((getScore('BAMLH0A0HYM2') + getScore('BAMLC0A4CBBB') + getScore('HY_IG_SPREAD')) / 3),
    VOL_EQUITY: Math.round((getScore('VIXCLS') + getScore('VXVCLS') + getScore('VIX_TERM_SPREAD')) / 3),
    VOL_RATES: Math.round((getScore('MOVE_INDEX') || 50 + getScore('DGS10') * 0.5) / 1.5),
  };

  // Calculate overall CSI score (average of pillars)
  const pillarValues = Object.values(pillarScores);
  const csiScore = Math.round(pillarValues.reduce((a, b) => a + b, 0) / pillarValues.length);

  // Determine regime
  let regime = 'NORMAL';
  if (csiScore >= 80) regime = 'CRISIS';
  else if (csiScore >= 65) regime = 'HIGH';
  else if (csiScore >= 50) regime = 'ELEVATED';
  else if (csiScore < 35) regime = 'CALM';

  // Build pillar results
  const pillars = {};
  for (const [pillarId, score] of Object.entries(pillarScores)) {
    pillars[pillarId] = {
      pillar: pillarId,
      score,
      signalCount: 4,
      familyCount: 3,
      freshFraction: 0.95,
      confidence: 0.9,
      isDegraded: false,
      contributions: []
    };
  }

  // Build top drivers from high-contribution signals
  const driverCandidates = [
    { signalId: 'NET_LIQUIDITY', pillar: 'LIQUIDITY' },
    { signalId: 'VIXCLS', pillar: 'VOL_EQUITY' },
    { signalId: 'BAMLH0A0HYM2', pillar: 'CREDIT' },
    { signalId: 'CURVE_2S10S', pillar: 'RATES_CURVE' },
    { signalId: 'SOFR', pillar: 'FUNDING' },
    { signalId: 'DGS10', pillar: 'RATES_CURVE' },
    { signalId: 'WALCL', pillar: 'LIQUIDITY' },
    { signalId: 'VXVCLS', pillar: 'VOL_EQUITY' },
  ];

  const topDrivers = driverCandidates.map(d => {
    const signal = signals[d.signalId];
    const score = getScore(d.signalId);
    return {
      signalId: d.signalId,
      name: signal?.name || d.signalId,
      pillar: d.pillar,
      familyId: d.signalId,
      contribution: score,
      delta20d: (Math.random() - 0.4) * 15, // Mock 20d change
      driverScore: score + Math.random() * 10,
      lastUpdated: new Date(),
    };
  }).sort((a, b) => b.driverScore - a.driverScore).slice(0, 8);

  return {
    date: new Date().toISOString().split('T')[0],
    score: csiScore,
    regime,
    confidence: 0.88,
    impulse: (Math.random() - 0.5) * 10,
    acceleration: (Math.random() - 0.5) * 5,
    pillars,
    topDrivers,
    metadata: {
      signalCount: Object.keys(signals).length,
      staleCount: 2,
      degradedPillars: []
    }
  };
}

/**
 * Calculates component stress scores from signals
 */
function calculateStressComponents(signals) {
  const getScore = (signalId) => {
    const signal = signals[signalId];
    if (!signal?.current?.level) return 30;
    const levelScore = { low: 10, calm: 15, normal: 25, elevated: 55, high: 75, stressed: 80, extreme: 90, crisis: 95 };
    return levelScore[signal.current.level] || 30;
  };
  
  return {
    liquidity: Math.round((getScore('NET_LIQUIDITY') + getScore('WALCL') + getScore('WTREGEN')) / 3),
    rates: Math.round((getScore('CURVE_2S10S') + getScore('CURVE_3M10Y') + getScore('DGS10') + getScore('REAL_10Y')) / 4),
    credit: Math.round((getScore('HY_IG_SPREAD') + getScore('BAMLH0A0HYM2') + getScore('BBB_TREASURY_SPREAD')) / 3),
    volatility: Math.round((getScore('VIXCLS') + getScore('VXVCLS') + getScore('VIX_TERM_SPREAD')) / 3),
    macro: Math.round((getScore('NFCI') + getScore('STLFSI4') + getScore('UMCSENT')) / 3),
  };
}

/**
 * Calculates recession probability from signal data
 */
function calculateRecessionProbability(signals) {
  let probability = 20;
  
  const curve2s10s = signals.CURVE_2S10S;
  if (curve2s10s?.current?.value != null) {
    const spread = curve2s10s.current.value;
    if (spread < 0) probability += 30;
    else if (spread < 0.5) probability += 15;
  }
  
  const hySpread = signals.HY_SPREAD || signals.HY_IG_SPREAD;
  if (hySpread?.current?.level === 'high' || hySpread?.current?.level === 'extreme' || hySpread?.current?.level === 'stressed') {
    probability += 20;
  } else if (hySpread?.current?.level === 'elevated' || hySpread?.current?.level === 'wide') {
    probability += 10;
  }
  
  const fundingStress = signals.FUNDING_STRESS_INDEX;
  if (fundingStress?.current?.level === 'high' || fundingStress?.current?.level === 'extreme') {
    probability += 15;
  }
  
  const claims = signals.CLAIMS_ZSCORE;
  if (claims?.current?.value > 2) probability += 10;
  
  const value = Math.min(95, Math.max(5, probability));
  const status = getGaugeStatus(value, 'recession');
  
  return { value, status };
}

/**
 * Calculates signal strength meter data
 */
function calculateSignalStrength(flatSignals) {
  const signalList = Object.values(flatSignals);
  const total = signalList.length;
  let stressed = 0;
  let elevated = 0;
  let normal = 0;
  let calm = 0;
  let noData = 0;
  
  signalList.forEach(signal => {
    const level = signal?.current?.level;
    if (!level || !signal?.current?.value) {
      noData++;
    } else if (['extreme', 'crisis', 'stressed', 'high'].includes(level)) {
      stressed++;
    } else if (['elevated', 'wide'].includes(level)) {
      elevated++;
    } else if (['low', 'calm', 'tight'].includes(level)) {
      calm++;
    } else {
      normal++;
    }
  });
  
  return { total, stressed, elevated, normal, calm, noData, loaded: total - noData };
}

/**
 * Creates a short abbreviation for a signal ID
 * @param {string} id - Signal ID like NET_LIQUIDITY or VIX_ZSCORE
 * @returns {string} Short 3-5 char abbreviation
 */
function abbreviateSignalId(id) {
  // Special cases for common signals
  const abbreviations = {
    'NET_LIQUIDITY': 'NLQ',
    'TGA_BALANCE': 'TGA',
    'RRP_BALANCE': 'RRP',
    'FED_BALANCE': 'FED',
    'VIX_LEVEL': 'VIX',
    'VIX_ZSCORE': 'VIXz',
    'VIX_TERM': 'VIXt',
    'CURVE_2S10S': '2S10',
    'CURVE_3M10Y': '3M10',
    'HY_SPREAD': 'HY',
    'HY_IG_SPREAD': 'HYIG',
    'IG_SPREAD': 'IG',
    'SOFR_RATE': 'SOFR',
    'EFFR_RATE': 'EFFR',
    'MOVE_INDEX': 'MOVE',
    'DXY_INDEX': 'DXY',
    'GOLD_PRICE': 'GOLD',
    'OIL_PRICE': 'OIL',
    'BTC_PRICE': 'BTC',
    'SPX_LEVEL': 'SPX',
    'CAP_STRESS_INDEX': 'CAP',
    'CREDIT_STRESS_INDEX': 'CRED',
    'FUNDING_STRESS_INDEX': 'FUND',
    'LIQUIDITY_STRESS_INDEX': 'LIQX',
  };
  
  if (abbreviations[id]) return abbreviations[id];
  
  // For z-scores and changes, append indicator
  if (id.endsWith('_ZSCORE')) {
    const base = id.replace('_ZSCORE', '');
    const abbr = abbreviateSignalId(base);
    return abbr.length <= 3 ? abbr + 'z' : abbr.slice(0, 3) + 'z';
  }
  if (id.endsWith('_CHG') || id.endsWith('_CHANGE')) {
    const base = id.replace(/_CHG$|_CHANGE$/, '');
    const abbr = abbreviateSignalId(base);
    return abbr.length <= 3 ? abbr + 'Δ' : abbr.slice(0, 3) + 'Δ';
  }
  if (id.endsWith('_ROC')) {
    const base = id.replace('_ROC', '');
    const abbr = abbreviateSignalId(base);
    return abbr.length <= 3 ? abbr + 'r' : abbr.slice(0, 3) + 'r';
  }
  
  // Generic: take first letters of each word, max 4 chars
  const parts = id.split('_');
  if (parts.length === 1) return id.slice(0, 4).toUpperCase();
  
  // Take first letter of each part
  let abbr = parts.map(p => p[0]).join('').toUpperCase();
  if (abbr.length > 4) abbr = abbr.slice(0, 4);
  if (abbr.length < 2) abbr = id.slice(0, 4).toUpperCase();
  
  return abbr;
}

/**
 * Creates heatmap tile data from signals
 * Classifies each tile as derived or source data
 */
function createHeatmapData(flatSignals) {
  const noDataSignals = [];
  
  const tiles = Object.entries(flatSignals).map(([id, signal]) => {
    const level = signal?.current?.level;
    const value = signal?.current?.value;
    
    // Track signals without data
    const hasData = value !== undefined && value !== null;
    if (!hasData) {
      noDataSignals.push(id);
    }
    
    // Classify as derived or source
    const isCap = isCapComputed(id);
    
    return {
      id,
      abbr: abbreviateSignalId(id),
      name: signal.name || id,
      category: signal.category,
      level: level || 'unknown',
      value: value,
      hasData,
      isCap,
      color: level ? getLevelColor(level) : 'var(--tg-text-faint)',
    };
  });
  
  // Log signals without data for debugging
  if (noDataSignals.length > 0) {
    console.warn(`[Heatmap] ${noDataSignals.length} signals with NO DATA:`, noDataSignals);
  }
  
  return tiles;
}

// ============================================================================
// SIGNAL INTELLIGENCE BRIEFING (Marketing Section)
// ============================================================================

/**
 * Creates the Signal Intelligence Briefing section
 * Marketing section explaining the signal system, tiers, and value proposition
 * @returns {HTMLElement}
 */
function createSignalIntelligenceBriefing() {
  // Tier pyramid data
  const tiers = [
    { 
      id: 'flagship', 
      name: 'Flagship Composites', 
      icon: '👑', 
      count: 5,
      desc: 'Our most powerful signals combining multiple data sources into actionable intelligence',
      examples: ['CAP Stress Index', 'Net Liquidity', 'Credit Stress Index']
    },
    { 
      id: 'primary', 
      name: 'Primary Signals', 
      icon: '⚡', 
      count: 8,
      desc: 'Direct market indicators with proven predictive value',
      examples: ['VIX Level', 'Yield Curve 2s10s', 'HY Spreads']
    },
    { 
      id: 'derived', 
      name: 'Derived Analytics', 
      icon: '📊', 
      count: 10,
      desc: 'Calculated metrics and spreads that reveal hidden market dynamics',
      examples: ['Real Yields', 'Curve Spreads', 'Term Spreads']
    },
    { 
      id: 'zscore', 
      name: 'Z-Score Signals', 
      icon: '📈', 
      count: 6,
      desc: 'Statistical measures showing deviation from historical norms',
      examples: ['VIX Z-Score', 'Liquidity Z-Score', 'Claims Z-Score']
    },
    { 
      id: 'roc', 
      name: 'Rate of Change', 
      icon: '🔄', 
      count: 4,
      desc: 'Momentum indicators tracking velocity of market movements',
      examples: ['Fed Balance RoC', 'Claims RoC']
    },
    { 
      id: 'source', 
      name: 'Source Data', 
      icon: '🏛️', 
      count: 87,
      desc: 'Raw Federal Reserve data series updated daily',
      examples: ['WALCL', 'DGS10', 'VIXCLS']
    },
  ];
  
  // Stress level guide
  const stressLevels = [
    { level: 'calm', color: 'var(--tg-fog-green)', label: 'CALM', desc: 'Markets relaxed, risk appetite healthy' },
    { level: 'normal', color: 'var(--tg-liquidity-cyan)', label: 'NORMAL', desc: 'Typical market conditions' },
    { level: 'elevated', color: '#f97316', label: 'ELEVATED', desc: 'Heightened attention warranted' },
    { level: 'stressed', color: 'var(--tg-rust-red)', label: 'STRESSED', desc: 'Significant market pressure' },
    { level: 'crisis', color: 'var(--tg-flash-crash)', label: 'CRISIS', desc: 'Extreme conditions, capital preservation mode' },
  ];
  
  return el('section', { className: 'signals-section signal-intel-briefing' }, [
    // Section Header
    el('div', { className: 'intel-briefing__header' }, [
      el('div', { className: 'intel-briefing__label' }, ['SIGNAL INTELLIGENCE']),
      el('h2', { className: 'intel-briefing__title' }, ['Understanding Our Signals']),
      el('p', { className: 'intel-briefing__subtitle' }, [
        'A hierarchical system of macro indicators designed to detect market stress before it becomes obvious.'
      ]),
    ]),
    
    // Main Grid: Value Props + Tier Pyramid
    el('div', { className: 'intel-briefing__grid' }, [
      // Left: Value Proposition Cards
      el('div', { className: 'intel-briefing__value-props' }, [
        el('div', { className: 'value-prop-card' }, [
          el('div', { className: 'value-prop-card__icon' }, ['🎯']),
          el('h3', { className: 'value-prop-card__title' }, ['Early Warning System']),
          el('p', { className: 'value-prop-card__desc' }, [
            'Our signals are designed to flash warnings before traditional indicators react. The 2019 repo crisis, 2020 crash, and 2023 banking stress all showed in our data first.'
          ]),
          el('div', { className: 'value-prop-card__stat' }, ['3-5 DAY AVG LEAD TIME']),
          el('div', { className: 'value-prop-card__badges' }, [
            el('span', { className: 'value-prop-badge' }, ['✓ 2019 Repo']),
            el('span', { className: 'value-prop-badge' }, ['✓ 2020 Crash']),
            el('span', { className: 'value-prop-badge' }, ['✓ 2023 SVB']),
          ]),
        ]),
        el('div', { className: 'value-prop-card' }, [
          el('div', { className: 'value-prop-card__icon' }, ['🧮']),
          el('h3', { className: 'value-prop-card__title' }, ['Quantified Intuition']),
          el('p', { className: 'value-prop-card__desc' }, [
            "Turn market feel into actionable data. Each signal has documented thresholds based on historical stress events, removing emotion from the equation."
          ]),
          el('div', { className: 'value-prop-card__stat' }, ['12 MAJOR EVENTS BACKTESTED']),
        ]),
        el('div', { className: 'value-prop-card' }, [
          el('div', { className: 'value-prop-card__icon' }, ['🔗']),
          el('h3', { className: 'value-prop-card__title' }, ['Connected Intelligence']),
          el('p', { className: 'value-prop-card__desc' }, [
            'Signals are mapped to related indicators. When liquidity tightens, see which credit and funding metrics typically follow.'
          ]),
          el('div', { className: 'value-prop-card__stat' }, ['118 CONNECTED SIGNALS']),
        ]),
      ]),
      
      // Right: Tier Pyramid
      el('div', { className: 'intel-briefing__tier-pyramid' }, [
        el('h3', { className: 'tier-pyramid__title' }, ['Signal Hierarchy']),
        el('div', { className: 'tier-pyramid__list' }, 
          tiers.map((tier, idx) => 
            el('div', { 
              className: `tier-pyramid__tier tier-pyramid__tier--${tier.id}`,
              style: `--tier-width: ${100 - (idx * 12)}%`
            }, [
              el('div', { className: 'tier-pyramid__tier-header' }, [
                el('span', { className: 'tier-pyramid__tier-icon' }, [tier.icon]),
                el('span', { className: 'tier-pyramid__tier-name' }, [tier.name]),
                el('span', { className: 'tier-pyramid__tier-count' }, [`${tier.count}`]),
              ]),
              el('p', { className: 'tier-pyramid__tier-desc' }, [tier.desc]),
              el('div', { className: 'tier-pyramid__tier-examples' }, 
                tier.examples.map(ex => el('span', { className: 'tier-example' }, [ex]))
              ),
            ])
          )
        ),
      ]),
    ]),
    
    // Stress Level Guide
    el('div', { className: 'intel-briefing__stress-guide' }, [
      el('h3', { className: 'stress-guide__title' }, ['Reading Stress Levels']),
      el('p', { className: 'stress-guide__subtitle' }, [
        'Every signal is classified into one of five stress levels based on historical thresholds:'
      ]),
      el('div', { className: 'stress-guide__levels' }, 
        stressLevels.map(sl => 
          el('div', { className: `stress-guide__level stress-guide__level--${sl.level}` }, [
            el('div', { 
              className: 'stress-guide__level-indicator',
              style: `background: ${sl.color}`
            }),
            el('div', { className: 'stress-guide__level-content' }, [
              el('span', { className: 'stress-guide__level-label' }, [sl.label]),
              el('span', { className: 'stress-guide__level-desc' }, [sl.desc]),
            ]),
          ])
        )
      ),
    ]),
    
    // CTA Row
    el('div', { className: 'intel-briefing__cta' }, [
      el('p', { className: 'intel-briefing__cta-text' }, [
        'Hover over any signal for instant context. Click for detailed charts and historical analysis.'
      ]),
      el('div', { className: 'intel-briefing__cta-buttons' }, [
        el('button', { 
          className: 'intel-cta-btn intel-cta-btn--primary',
          onclick: openGlossaryModal
        }, [
          el('span', { className: 'intel-cta-btn__icon' }, ['📖']),
          'Browse Full Glossary'
        ]),
        el('a', { 
          className: 'intel-cta-btn intel-cta-btn--secondary',
          href: '#/manifesto'
        }, [
          el('span', { className: 'intel-cta-btn__icon' }, ['📜']),
          'Read Our Manifesto'
        ]),
      ]),
    ]),
  ]);
}

// ============================================================================
// UI COMPONENTS
// ============================================================================

/**
 * Creates the category tabs component
 */
function createCategoryTabs(activeCategory, onSelect) {
  const allTab = el('button', {
    className: `category-tab ${activeCategory === 'all' ? 'category-tab--active' : ''}`,
    onclick: () => onSelect('all'),
  }, [
    el('span', { className: 'category-tab__icon' }, ['🔮']),
    el('span', { className: 'category-tab__label' }, ['All']),
    el('span', { className: 'category-tab__count' }, [ALL_SIGNAL_IDS.length.toString()]),
  ]);
  
  const categoryTabs = CATEGORY_ORDER.map(catId => {
    const cat = CATEGORIES[catId];
    const count = SIGNAL_IDS[catId]?.length || 0;
    const isActive = activeCategory === catId;
    
    return el('button', {
      className: `category-tab ${isActive ? 'category-tab--active' : ''}`,
      style: isActive ? `--tab-accent: ${cat.color}` : '',
      onclick: () => onSelect(catId),
    }, [
      el('span', { className: 'category-tab__icon' }, [cat.icon]),
      el('span', { className: 'category-tab__label' }, [cat.name]),
      el('span', { className: 'category-tab__count' }, [count.toString()]),
    ]);
  });
  
  return el('div', { className: 'category-tabs-wrapper' }, [
    el('div', { className: 'category-tabs' }, [allTab, ...categoryTabs]),
  ]);
}

/**
 * Creates the search bar component
 */
function createSearchBar(onSearch) {
  const input = el('input', {
    type: 'text',
    className: 'search-bar__input',
    placeholder: 'Search signals... (e.g., VIX, liquidity, curve)',
    oninput: (e) => onSearch(e.target.value),
  });
  
  return el('div', { className: 'search-bar' }, [
    el('span', { className: 'search-bar__icon' }, ['🔍']),
    input,
    el('span', { className: 'search-bar__hint' }, ['Press / to focus']),
  ]);
}

/**
 * Handles stress level filter click
 * @param {string} level - 'stressed'|'elevated'|'normal'|'calm' or null to clear
 */
function handleStressLevelFilter(level) {
  // Toggle off if clicking the same level
  if (pageState.stressLevelFilter === level) {
    pageState.stressLevelFilter = null;
  } else {
    pageState.stressLevelFilter = level;
  }
  
  // Update legend item active states
  document.querySelectorAll('.legend-item').forEach(item => {
    item.classList.remove('legend-item--active');
  });
  
  if (pageState.stressLevelFilter) {
    const activeItem = document.querySelector(`.legend-item--${pageState.stressLevelFilter}`);
    if (activeItem) activeItem.classList.add('legend-item--active');
  }
  
  // Update heatmap visibility
  applyStressLevelFilter();
}

/**
 * Applies stress level filter to heatmap tiles
 */
function applyStressLevelFilter() {
  const tiles = document.querySelectorAll('.heatmap-tile');
  const filter = pageState.stressLevelFilter;
  
  // Level mapping: group related levels
  const levelMap = {
    'stressed': ['extreme', 'crisis', 'stressed', 'high'],
    'elevated': ['elevated', 'wide'],
    'normal': ['normal', 'flat'],
    'calm': ['low', 'calm', 'tight'],
  };
  
  tiles.forEach(tile => {
    const tileLevel = tile.dataset.level;
    
    if (!filter) {
      // No filter - show all
      tile.classList.remove('heatmap-tile--filtered-out');
    } else {
      // Check if tile matches filter
      const matchingLevels = levelMap[filter] || [filter];
      const matches = matchingLevels.includes(tileLevel);
      tile.classList.toggle('heatmap-tile--filtered-out', !matches);
    }
  });
  
  // Update visible count
  const visibleTiles = document.querySelectorAll('.heatmap-tile:not(.heatmap-tile--filtered-out)');
  const totalTiles = tiles.length;
  
  // Update filter indicator if it exists
  let filterIndicator = document.querySelector('.stress-filter-indicator');
  if (filter) {
    if (!filterIndicator) {
      filterIndicator = el('div', { className: 'stress-filter-indicator' }, []);
      const heatmapHeader = document.querySelector('.heatmap__header');
      if (heatmapHeader) heatmapHeader.appendChild(filterIndicator);
    }
    filterIndicator.innerHTML = `
      <span class="stress-filter-indicator__text">Showing: ${visibleTiles.length} of ${totalTiles} (${filter.toUpperCase()})</span>
      <button class="stress-filter-indicator__clear" title="Clear filter">×</button>
    `;
    filterIndicator.querySelector('.stress-filter-indicator__clear').onclick = () => handleStressLevelFilter(null);
  } else if (filterIndicator) {
    filterIndicator.remove();
  }
}

/**
 * Creates the signal strength meter component
 */
function createSignalStrengthMeter(data) {
  const { total, stressed, elevated, normal, calm, loaded } = data;
  const stressPercent = loaded > 0 ? Math.round((stressed / loaded) * 100) : 0;
  const elevatedPercent = loaded > 0 ? Math.round((elevated / loaded) * 100) : 0;
  const normalPercent = loaded > 0 ? Math.round((normal / loaded) * 100) : 0;
  const calmPercent = loaded > 0 ? Math.round((calm / loaded) * 100) : 0;
  
  return el('div', { className: 'signal-strength-meter', 'data-component': 'strength-meter' }, [
    el('div', { className: 'strength-meter__header' }, [
      el('h3', { className: 'strength-meter__title' }, ['Signal Health']),
      el('span', { className: 'strength-meter__subtitle' }, [`${loaded} of ${total} signals reporting`]),
    ]),
    el('div', { className: 'strength-meter__bar' }, [
      el('div', { 
        className: 'strength-meter__segment strength-meter__segment--stressed',
        style: `width: ${stressPercent}%`,
        title: `${stressed} stressed`,
      }),
      el('div', { 
        className: 'strength-meter__segment strength-meter__segment--elevated',
        style: `width: ${elevatedPercent}%`,
        title: `${elevated} elevated`,
      }),
      el('div', { 
        className: 'strength-meter__segment strength-meter__segment--normal',
        style: `width: ${normalPercent}%`,
        title: `${normal} normal`,
      }),
      el('div', { 
        className: 'strength-meter__segment strength-meter__segment--calm',
        style: `width: ${calmPercent}%`,
        title: `${calm} calm/low`,
      }),
    ]),
    el('div', { className: 'strength-meter__legend' }, [
      el('button', { 
        className: 'legend-item legend-item--stressed',
        onclick: () => handleStressLevelFilter('stressed'),
        title: 'Click to filter stressed signals'
      }, [
        el('span', { className: 'legend-dot' }),
        `${stressed} Stressed`,
      ]),
      el('button', { 
        className: 'legend-item legend-item--elevated',
        onclick: () => handleStressLevelFilter('elevated'),
        title: 'Click to filter elevated signals'
      }, [
        el('span', { className: 'legend-dot' }),
        `${elevated} Elevated`,
      ]),
      el('button', { 
        className: 'legend-item legend-item--normal',
        onclick: () => handleStressLevelFilter('normal'),
        title: 'Click to filter normal signals'
      }, [
        el('span', { className: 'legend-dot' }),
        `${normal} Normal`,
      ]),
      el('button', { 
        className: 'legend-item legend-item--calm',
        onclick: () => handleStressLevelFilter('calm'),
        title: 'Click to filter calm signals'
      }, [
        el('span', { className: 'legend-dot' }),
        `${calm} Calm`,
      ]),
    ]),
  ]);
}

/**
 * Formats a value for display in the heatmap tooltip
 */
function formatHeatmapValue(value, id) {
  if (value === null || value === undefined) return 'N/A';
  
  // Format based on signal type
  if (id.includes('ZSCORE') || id.includes('_Z')) {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}σ`;
  }
  if (id.includes('SPREAD') || id.includes('RATE') || id.includes('YIELD')) {
    return `${value.toFixed(2)}%`;
  }
  if (id.includes('PRICE') || id.includes('INDEX') || id.includes('LEVEL')) {
    return value >= 1000 ? value.toLocaleString(undefined, {maximumFractionDigits: 0}) : value.toFixed(2);
  }
  if (id.includes('BALANCE') || id.includes('LIQUIDITY')) {
    // Assume in millions, convert to trillions
    const inTrillions = value / 1000000;
    return `${inTrillions.toFixed(2)}T`;
  }
  
  return typeof value === 'number' ? value.toFixed(2) : String(value);
}

/**
 * Creates a single heatmap tile
 */
function createHeatmapTile(signal, onSignalClick) {
  const signalDoc = getSignalDoc(signal.id);
  const tier = signalDoc?.tier || inferTier(signal.id, signal.isCap);
  
  const classes = [
    'heatmap-tile',
    `heatmap-tile--${signal.level}`,
    `heatmap-tile--tier-${tier}`,
    !signal.hasData ? 'heatmap-tile--no-data' : '',
  ].filter(Boolean).join(' ');
  
  const tile = el('div', {
    className: classes,
    style: `--tile-color: ${signal.color}`,
    onclick: () => onSignalClick(signal.id),
    tabindex: '0',
    role: 'button',
    'aria-label': `${signal.name} - ${signal.level}`,
    'data-level': signal.level,
    'data-tier': tier,
  }, [
    el('span', { className: 'heatmap-tile__abbr' }, [signal.abbr || signal.id.slice(0, 4)]),
  ]);
  
  // Add tooltip event listeners
  tile.addEventListener('mouseenter', () => showHeatmapTooltip(signal, tile));
  tile.addEventListener('mouseleave', hideHeatmapTooltip);
  tile.addEventListener('focus', () => showHeatmapTooltip(signal, tile));
  tile.addEventListener('blur', hideHeatmapTooltip);
  
  return tile;
}

/**
 * Tier configuration for heatmap sections with tooltip descriptions
 */
const HEATMAP_TIERS = [
  { 
    id: 'flagship', 
    name: 'Flagship Composites', 
    icon: '👑', 
    color: 'var(--tg-lantern-gold)',
    tooltip: 'Proprietary composite indices combining multiple signals into actionable stress readings'
  },
  { 
    id: 'primary', 
    name: 'Primary Signals', 
    icon: '⚡', 
    color: 'var(--tg-liquidity-cyan)',
    tooltip: 'Core market indicators directly reflecting liquidity, rates, and credit conditions'
  },
  { 
    id: 'derived', 
    name: 'Derived Analytics', 
    icon: '📊', 
    color: 'var(--tg-fog-green)',
    tooltip: 'Calculated metrics derived from primary signals (spreads, ratios, transformations)'
  },
  { 
    id: 'zscore', 
    name: 'Z-Score Signals', 
    icon: '📈', 
    color: '#a78bfa',
    tooltip: 'Normalized signals showing standard deviations from historical mean for outlier detection'
  },
  { 
    id: 'roc', 
    name: 'Rate of Change', 
    icon: '🔄', 
    color: '#f97316',
    tooltip: 'Momentum indicators showing velocity of change over defined periods'
  },
  { 
    id: 'source', 
    name: 'Source Data', 
    icon: '🏛️', 
    color: 'rgba(151, 184, 160, 0.6)',
    tooltip: 'Raw data feeds from FRED and other sources before CAP transformations'
  },
];

/**
 * Infers signal tier based on ID patterns if no documentation
 * @param {string} signalId - Signal ID
 * @param {boolean} isCap - Whether it's a CAP computed signal
 * @returns {string} Inferred tier
 */
function inferTier(signalId, isCap) {
  const id = signalId.toUpperCase();
  
  // Z-Score pattern
  if (id.includes('_ZSCORE') || id.includes('_Z') || id.endsWith('Z')) {
    return 'zscore';
  }
  
  // Rate of Change pattern
  if (id.includes('_ROC') || id.includes('_CHANGE') || id.includes('_CHG')) {
    return 'roc';
  }
  
  // CAP computed signals without specific pattern → derived
  if (isCap) {
    return 'derived';
  }
  
  // Default to source
  return 'source';
}

/**
 * Groups heatmap data by tier
 * @param {Array} data - Heatmap tile data
 * @returns {Object} Signals grouped by tier
 */
function groupByTier(data) {
  const groups = {
    flagship: [],
    primary: [],
    derived: [],
    zscore: [],
    roc: [],
    source: [],
  };
  
  data.forEach(signal => {
    const signalDoc = getSignalDoc(signal.id);
    let tier = signalDoc?.tier;
    
    // If no documentation, infer tier from signal ID patterns
    if (!tier) {
      tier = inferTier(signal.id, signal.isCap);
    }
    
    // Normalize tier names
    if (tier === 'z-score') {
      groups.zscore.push(signal);
    } else if (groups[tier]) {
      groups[tier].push(signal);
    } else {
      groups.source.push(signal);
    }
  });
  
  return groups;
}

/**
 * Creates the stress heatmap grid organized by signal tier
 */
/**
 * Creates the heatmap footer with integrated Signal Health bar
 * This replaces the simple legend and integrates the Signal Health meter
 * @param {Object} strengthData - Signal strength data
 * @returns {HTMLElement}
 */
function createHeatmapFooter(strengthData) {
  const { total, stressed, elevated, normal, calm, loaded, noData } = strengthData;
  const stressPercent = loaded > 0 ? Math.round((stressed / loaded) * 100) : 0;
  const elevatedPercent = loaded > 0 ? Math.round((elevated / loaded) * 100) : 0;
  const normalPercent = loaded > 0 ? Math.round((normal / loaded) * 100) : 0;
  const calmPercent = loaded > 0 ? Math.round((calm / loaded) * 100) : 0;
  
  return el('div', { className: 'heatmap-footer', 'data-component': 'heatmap-footer' }, [
    // Header with title and reporting count
    el('div', { className: 'heatmap-footer__header' }, [
      el('h3', { className: 'heatmap-footer__title' }, ['Signal Health']),
      el('span', { className: 'heatmap-footer__reporting' }, [
        `${loaded} of ${total} signals reporting`
      ]),
    ]),
    
    // Status row (for clear filter button)
    pageState.stressLevelFilter ? el('div', { className: 'heatmap-footer__status' }, [
      el('button', { 
        className: 'heatmap-footer__clear-filter',
        onclick: () => handleStressLevelFilter(null),
        title: 'Clear filter'
      }, ['× Clear filter']),
    ]) : null,
    
    // Stacked bar
    el('div', { className: 'heatmap-footer__bar' }, [
      el('div', { 
        className: 'heatmap-footer__segment heatmap-footer__segment--stressed',
        style: `width: ${stressPercent}%`,
        title: `${stressed} stressed (${stressPercent}%)`,
      }),
      el('div', { 
        className: 'heatmap-footer__segment heatmap-footer__segment--elevated',
        style: `width: ${elevatedPercent}%`,
        title: `${elevated} elevated (${elevatedPercent}%)`,
      }),
      el('div', { 
        className: 'heatmap-footer__segment heatmap-footer__segment--normal',
        style: `width: ${normalPercent}%`,
        title: `${normal} normal (${normalPercent}%)`,
      }),
      el('div', { 
        className: 'heatmap-footer__segment heatmap-footer__segment--calm',
        style: `width: ${calmPercent}%`,
        title: `${calm} calm (${calmPercent}%)`,
      }),
    ]),
    
    // Interactive legend (click to filter)
    el('div', { className: 'heatmap-footer__legend' }, [
      el('button', { 
        className: `heatmap-footer__legend-btn heatmap-footer__legend-btn--stressed ${pageState.stressLevelFilter === 'stressed' ? 'heatmap-footer__legend-btn--active' : ''}`,
        onclick: () => handleStressLevelFilter('stressed'),
        title: 'Click to highlight stressed signals'
      }, [
        el('span', { className: 'legend-dot legend-dot--stressed' }),
        el('span', { className: 'legend-count' }, [String(stressed)]),
        el('span', { className: 'legend-label' }, ['Stressed']),
      ]),
      el('button', { 
        className: `heatmap-footer__legend-btn heatmap-footer__legend-btn--elevated ${pageState.stressLevelFilter === 'elevated' ? 'heatmap-footer__legend-btn--active' : ''}`,
        onclick: () => handleStressLevelFilter('elevated'),
        title: 'Click to highlight elevated signals'
      }, [
        el('span', { className: 'legend-dot legend-dot--elevated' }),
        el('span', { className: 'legend-count' }, [String(elevated)]),
        el('span', { className: 'legend-label' }, ['Elevated']),
      ]),
      el('button', { 
        className: `heatmap-footer__legend-btn heatmap-footer__legend-btn--normal ${pageState.stressLevelFilter === 'normal' ? 'heatmap-footer__legend-btn--active' : ''}`,
        onclick: () => handleStressLevelFilter('normal'),
        title: 'Click to highlight normal signals'
      }, [
        el('span', { className: 'legend-dot legend-dot--normal' }),
        el('span', { className: 'legend-count' }, [String(normal)]),
        el('span', { className: 'legend-label' }, ['Normal']),
      ]),
      el('button', { 
        className: `heatmap-footer__legend-btn heatmap-footer__legend-btn--calm ${pageState.stressLevelFilter === 'calm' ? 'heatmap-footer__legend-btn--active' : ''}`,
        onclick: () => handleStressLevelFilter('calm'),
        title: 'Click to highlight calm signals'
      }, [
        el('span', { className: 'legend-dot legend-dot--calm' }),
        el('span', { className: 'legend-count' }, [String(calm)]),
        el('span', { className: 'legend-label' }, ['Calm']),
      ]),
      noData > 0 ? el('span', { className: 'heatmap-footer__legend-btn heatmap-footer__legend-btn--nodata' }, [
        el('span', { className: 'legend-dot legend-dot--nodata' }),
        el('span', { className: 'legend-count' }, [String(noData)]),
        el('span', { className: 'legend-label' }, ['No Data']),
      ]) : null,
    ].filter(Boolean)),
    
    // Hint
    el('div', { className: 'heatmap-footer__hint' }, [
      'Click levels to filter • Hover tiles for details'
    ]),
  ].filter(Boolean));
}

/**
 * Updates the heatmap footer with new strength data
 * @param {Object} data - Signal strength data
 */
function updateHeatmapFooter(data) {
  const footer = document.querySelector('[data-component="heatmap-footer"]');
  if (!footer) return;
  
  const { total, stressed, elevated, normal, calm, loaded, noData } = data;
  const stressPercent = loaded > 0 ? Math.round((stressed / loaded) * 100) : 0;
  const elevatedPercent = loaded > 0 ? Math.round((elevated / loaded) * 100) : 0;
  const normalPercent = loaded > 0 ? Math.round((normal / loaded) * 100) : 0;
  const calmPercent = loaded > 0 ? Math.round((calm / loaded) * 100) : 0;
  
  // Update reporting text
  const reportingEl = footer.querySelector('.heatmap-footer__reporting');
  if (reportingEl) reportingEl.textContent = `${loaded} of ${total} signals reporting`;
  
  // Update bar segments
  const segments = footer.querySelectorAll('.heatmap-footer__segment');
  if (segments[0]) segments[0].style.width = `${stressPercent}%`;
  if (segments[1]) segments[1].style.width = `${elevatedPercent}%`;
  if (segments[2]) segments[2].style.width = `${normalPercent}%`;
  if (segments[3]) segments[3].style.width = `${calmPercent}%`;
  
  // Update legend counts
  const legendBtns = footer.querySelectorAll('.heatmap-footer__legend-btn');
  const counts = [stressed, elevated, normal, calm];
  legendBtns.forEach((btn, i) => {
    const countEl = btn.querySelector('.legend-count');
    if (countEl && counts[i] !== undefined) countEl.textContent = String(counts[i]);
  });
}

function createStressHeatmap(data, onSignalClick, strengthData) {
  // Group signals by tier
  const tierGroups = groupByTier(data);
  
  // Create tooltip
  heatmapTooltip = createHeatmapTooltip();
  
  // Create tier sections
  const tierSections = HEATMAP_TIERS.map(tier => {
    const signals = tierGroups[tier.id] || [];
    if (signals.length === 0) return null;
    
    const tiles = signals.map(signal => createHeatmapTile(signal, onSignalClick));
    
    return el('div', { 
      className: `heatmap-section heatmap-section--${tier.id}`,
      'data-tier': tier.id
    }, [
      el('div', { 
        className: 'heatmap-section__header',
        title: tier.tooltip,
        'data-tooltip': tier.tooltip
      }, [
        el('span', { className: 'heatmap-section__icon' }, [tier.icon]),
        el('span', { className: 'heatmap-section__title' }, [tier.name]),
        el('span', { className: 'heatmap-section__count' }, [`${signals.length}`]),
      ]),
      el('div', { className: `heatmap__grid heatmap__grid--${tier.id}` }, tiles),
    ]);
  }).filter(Boolean);
  
  // Create footer with integrated Signal Health
  const footer = createHeatmapFooter(strengthData);
  
  return el('div', { className: 'stress-heatmap', 'data-component': 'heatmap' }, [
    // Header
    el('div', { className: 'heatmap__header' }, [
      el('h3', { className: 'heatmap__title' }, ['Stress Heatmap']),
      el('span', { className: 'heatmap__subtitle' }, ['Signal Health Overview']),
    ]),
    
    // Tier Sections
    ...tierSections,
    
    // Footer with integrated Signal Health (replaces old legend)
    footer,
    
    heatmapTooltip,
  ]);
}

/**
 * Updates the signal strength meter
 */
function updateSignalStrengthMeter(data) {
  const meter = document.querySelector('[data-component="strength-meter"]');
  if (!meter) return;
  
  const { total, stressed, elevated, normal, calm, loaded } = data;
  const stressPercent = loaded > 0 ? Math.round((stressed / loaded) * 100) : 0;
  const elevatedPercent = loaded > 0 ? Math.round((elevated / loaded) * 100) : 0;
  const normalPercent = loaded > 0 ? Math.round((normal / loaded) * 100) : 0;
  const calmPercent = loaded > 0 ? Math.round((calm / loaded) * 100) : 0;
  
  meter.querySelector('.strength-meter__subtitle').textContent = `${loaded} of ${total} signals reporting`;
  
  const segments = meter.querySelectorAll('.strength-meter__segment');
  segments[0].style.width = `${stressPercent}%`;
  segments[1].style.width = `${elevatedPercent}%`;
  segments[2].style.width = `${normalPercent}%`;
  segments[3].style.width = `${calmPercent}%`;
  
  const legends = meter.querySelectorAll('.legend-item');
  legends[0].lastChild.textContent = ` ${stressed} Stressed`;
  legends[1].lastChild.textContent = ` ${elevated} Elevated`;
  legends[2].lastChild.textContent = ` ${normal} Normal`;
  legends[3].lastChild.textContent = ` ${calm} Calm`;
}

/**
 * Updates the stress heatmap with tier-based organization
 */
function updateStressHeatmap(data) {
  const heatmap = document.querySelector('[data-component="heatmap"]');
  if (!heatmap) return;
  
  // Group signals by tier
  const tierGroups = groupByTier(data);
  
  // Count stats for debugging
  const stats = { total: data.length, withData: 0, noData: 0, byTier: {}, byLevel: {} };
  
  // Helper to create and append tile
  const createTile = (signal) => {
    if (signal.hasData) stats.withData++;
    else stats.noData++;
    stats.byLevel[signal.level] = (stats.byLevel[signal.level] || 0) + 1;
    
    const signalDoc = getSignalDoc(signal.id);
    const tier = signalDoc?.tier || inferTier(signal.id, signal.isCap);
    
    const classes = [
      'heatmap-tile',
      `heatmap-tile--${signal.level}`,
      `heatmap-tile--tier-${tier}`,
      !signal.hasData ? 'heatmap-tile--no-data' : '',
    ].filter(Boolean).join(' ');
    
    const tile = el('div', {
      className: classes,
      style: `--tile-color: ${signal.color}`,
      tabindex: '0',
      role: 'button',
      'aria-label': `${signal.name} - ${signal.level}`,
      'data-level': signal.level,
      'data-tier': tier,
      onclick: () => {
        const fullSignal = pageState.flatSignals[signal.id];
        if (fullSignal) {
          openExpandedChartModal(fullSignal, []);
        }
      },
    }, [
      el('span', { className: 'heatmap-tile__abbr' }, [signal.abbr || signal.id.slice(0, 4)]),
    ]);
    
    // Add tooltip event listeners
    tile.addEventListener('mouseenter', () => showHeatmapTooltip(signal, tile));
    tile.addEventListener('mouseleave', hideHeatmapTooltip);
    tile.addEventListener('focus', () => showHeatmapTooltip(signal, tile));
    tile.addEventListener('blur', hideHeatmapTooltip);
    
    return tile;
  };
  
  // Update each tier section
  HEATMAP_TIERS.forEach(tier => {
    const signals = tierGroups[tier.id] || [];
    stats.byTier[tier.id] = signals.length;
    
    // Find or create the section
    let section = heatmap.querySelector(`[data-tier="${tier.id}"]`);
    
    if (signals.length === 0) {
      // Remove section if no signals
      if (section) section.remove();
      return;
    }
    
    // Create section if it doesn't exist
    if (!section) {
      section = el('div', { 
        className: `heatmap-section heatmap-section--${tier.id}`,
        'data-tier': tier.id
      }, [
        el('div', { 
          className: 'heatmap-section__header',
          title: tier.tooltip,
          'data-tooltip': tier.tooltip
        }, [
          el('span', { className: 'heatmap-section__icon' }, [tier.icon]),
          el('span', { className: 'heatmap-section__title' }, [tier.name]),
          el('span', { className: 'heatmap-section__count' }, [`${signals.length}`]),
        ]),
        el('div', { className: `heatmap__grid heatmap__grid--${tier.id}` }),
      ]);
      
      // Insert before legend
      const legend = heatmap.querySelector('.heatmap__legend');
      if (legend) {
        heatmap.insertBefore(section, legend);
      } else {
        heatmap.appendChild(section);
      }
    }
    
    // Clear and populate grid
    const grid = section.querySelector(`.heatmap__grid--${tier.id}`);
    if (grid) {
      grid.innerHTML = '';
      signals.forEach(signal => {
        grid.appendChild(createTile(signal));
      });
    }
    
    // Update count
    const countEl = section.querySelector('.heatmap-section__count');
    if (countEl) countEl.textContent = `${signals.length}`;
  });
  
  console.log('[Heatmap] Updated by tier:', stats);
}

// ============================================================================
// FILTERING & SEARCH
// ============================================================================

/**
 * Filters and re-renders signals based on category and search
 */
function filterSignals() {
  const { activeCategory, searchQuery, signals } = pageState;
  
  // Show/hide category sections
  CATEGORY_ORDER.forEach(catId => {
    const section = document.querySelector(`[data-category="${catId}"]`);
    if (!section) return;
    
    const matchesCategory = activeCategory === 'all' || activeCategory === catId;
    
    if (!matchesCategory) {
      section.style.display = 'none';
      return;
    }
    
    section.style.display = '';
    
    // Filter cards within section by search
    if (searchQuery) {
      const cards = section.querySelectorAll('.signal-card');
      cards.forEach(card => {
        const signalId = card.dataset.signalId || '';
        const signalName = card.querySelector('.signal-card__title')?.textContent || '';
        const matches = signalId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       signalName.toLowerCase().includes(searchQuery.toLowerCase());
        card.style.display = matches ? '' : 'none';
      });
    } else {
      const cards = section.querySelectorAll('.signal-card');
      cards.forEach(card => card.style.display = '');
    }
  });
  
  // Update visible count
  updateVisibleCount();
}

/**
 * Updates the visible signal count badge
 */
function updateVisibleCount() {
  const visibleCards = document.querySelectorAll('.signal-card:not([style*="display: none"])');
  const countBadge = document.querySelector('.visible-count-badge');
  if (countBadge) {
    countBadge.textContent = `${visibleCards.length} Visible`;
  }
}

/**
 * Handles category tab selection
 */
function handleCategorySelect(category) {
  pageState.activeCategory = category;
  
  // Update tab UI
  document.querySelectorAll('.category-tab').forEach(tab => {
    tab.classList.remove('category-tab--active');
  });
  
  const tabs = document.querySelectorAll('.category-tab');
  const index = category === 'all' ? 0 : CATEGORY_ORDER.indexOf(category) + 1;
  if (tabs[index]) {
    tabs[index].classList.add('category-tab--active');
  }
  
  filterSignals();
}

/**
 * Handles search input
 */
function handleSearch(query) {
  pageState.searchQuery = query;
  filterSignals();
}

// ============================================================================
// DATA LOADING
// ============================================================================

/**
 * Preloads history data for heatmap tooltips
 * @param {string[]} signalIds - Signal IDs to load history for
 */
async function preloadHeatmapHistory(signalIds) {
  const BATCH_SIZE = 10;
  const DELAY_BETWEEN_BATCHES = 100;
  
  // Skip signals that already have history OR don't have history endpoints (derived signals)
  const needsLoading = signalIds.filter(id => 
    hasHistoryEndpoint(id) && 
    (!pageState.history[id] || pageState.history[id].length === 0)
  );
  
  if (needsLoading.length === 0) {
    console.log('[Heatmap] All history already loaded');
    return;
  }
  
  console.log(`[Heatmap] Preloading history for ${needsLoading.length} signals...`);
  
  // Process in batches to avoid overwhelming the API
  for (let i = 0; i < needsLoading.length; i += BATCH_SIZE) {
    const batch = needsLoading.slice(i, i + BATCH_SIZE);
    
    const batchPromises = batch.map(async (signalId) => {
      try {
        const history = await fetchSignalHistory(signalId, { days: 30 });
        return { signalId, data: history.data || history || [] };
      } catch (e) {
        // Silent fail for background loading
        return { signalId, data: [] };
      }
    });
    
    const results = await Promise.all(batchPromises);
    results.forEach(r => {
      if (r.data.length > 0) {
        pageState.history[r.signalId] = r.data;
      }
    });
    
    // Small delay between batches
    if (i + BATCH_SIZE < needsLoading.length) {
      await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
    }
  }
  
  console.log(`[Heatmap] Preloaded history for ${Object.keys(pageState.history).length} signals`);
}

/**
 * Loads all signal data and updates the page
 */
async function loadSignalData() {
  console.log('[Signals] Loading signal data...');
  
  try {
    const result = await preloadAllSignals();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to load signals');
    }
    
    pageState.signals = result.signals;
    pageState.lastUpdated = new Date();
    pageState.loading = false;
    
    // Flatten signals for calculations
    const flatSignals = {};
    Object.values(result.signals).forEach(categorySignals => {
      categorySignals.forEach(signal => {
        flatSignals[signal.signal_id] = signal;
      });
    });
    pageState.flatSignals = flatSignals;
    
    // Update CSI widget with computed data from signals
    if (csiWidgetInstance) {
      const csiData = generateCSIFromSignals(flatSignals);
      csiWidgetInstance.update(csiData);
      console.log('[Signals] CSI widget updated:', csiData.score, csiData.regime);
    }
    
    const recessionProb = calculateRecessionProbability(flatSignals);
    const recessionGaugeEl = document.querySelector('[data-gauge-id="recession-probability"]');
    if (recessionGaugeEl) updateCompositeGauge(recessionGaugeEl, recessionProb.value, recessionProb.status);
    
    // Update heatmap footer with strength data (now integrated in heatmap)
    const strengthData = calculateSignalStrength(flatSignals);
    updateHeatmapFooter(strengthData);
    
    // Update Data Freshness widget
    if (dataFreshnessInstance) {
      dataFreshnessInstance.update({
        loaded: strengthData.loaded,
        total: strengthData.total,
        lastUpdated: pageState.lastUpdated
      });
    }
    
    // Update CSI History Chart with current score
    if (csiHistoryChartInstance) {
      const csiData = generateCSIFromSignals(flatSignals);
      csiHistoryChartInstance.update({
        currentScore: csiData.score,
        currentRegime: csiData.regime
      });
    }
    
    // Update stress heatmap
    const heatmapData = createHeatmapData(flatSignals);
    updateStressHeatmap(heatmapData);
    
    // Preload history for heatmap tooltips (async, don't block)
    preloadHeatmapHistory(Object.keys(flatSignals));
    
    // Update signal cards by category
    for (const [category, signals] of Object.entries(result.signals)) {
      const sectionEl = document.querySelector(`[data-category="${category}"]`);
      if (!sectionEl) continue;
      
      updateCategorySection(sectionEl, signals);
      
      const gridEl = sectionEl.querySelector('.category-section__grid');
      if (!gridEl) continue;
      
      gridEl.innerHTML = '';
      
      // Load history for each signal (limit concurrent requests)
      // Only fetch history for signals that have history endpoints (skip derived signals)
      const signalsWithHistory = signals.filter(s => hasHistoryEndpoint(s.signal_id)).slice(0, 20);
      const historyPromises = signalsWithHistory.map(async (signal) => {
        try {
          const history = await fetchSignalHistory(signal.signal_id, { days: 30 });
          return { signalId: signal.signal_id, data: history.data || history || [] };
        } catch (e) {
          console.warn(`Failed to load history for ${signal.signal_id}:`, e);
          return { signalId: signal.signal_id, data: [] };
        }
      });
      
      const historyResults = await Promise.all(historyPromises);
      const historyMap = {};
      historyResults.forEach(r => { 
        historyMap[r.signalId] = r.data;
        // Cache in pageState for tooltip access
        pageState.history[r.signalId] = r.data;
      });
      
      // Create signal cards
      signals.forEach(signal => {
        const historyData = historyMap[signal.signal_id] || [];
        const card = createSignalCard(signal, historyData, (s) => {
          openExpandedChartModal(s, historyData);
        });
        card.dataset.signalId = signal.signal_id;
        
        // Attach tooltip handlers (Phase 5)
        attachCardTooltipHandlers(card, signal.signal_id, signal);
        
        gridEl.appendChild(card);
      });
    }
    
    // Update header badge
    const liveBadge = document.querySelector('.data-source-pill');
    if (liveBadge) {
      liveBadge.textContent = '🟢 LIVE from API';
      liveBadge.classList.remove('data-source-pill--error');
    }
    
    // Apply any active filters
    filterSignals();
    
    console.log('[Signals] Data loaded successfully');
    
  } catch (error) {
    console.error('[Signals] Failed to load data:', error);
    pageState.error = error.message;
    pageState.loading = false;
    
    const liveBadge = document.querySelector('.data-source-pill');
    if (liveBadge) {
      liveBadge.textContent = '🔴 API Error';
      liveBadge.classList.add('data-source-pill--error');
    }
  }
}

// ============================================================================
// PAGE RENDER
// ============================================================================

/**
 * Renders the Signals page
 */
export function renderSignalsPage() {
  pageState = { 
    signals: {}, 
    flatSignals: {},
    history: {}, 
    loading: true, 
    error: null, 
    lastUpdated: null,
    activeCategory: 'all',
    searchQuery: '',
    stressLevelFilter: null,
  };
  
  setTimeout(() => loadSignalData(), 100);
  
  // Build category sections with placeholders
  const categorySections = CATEGORY_ORDER.map(categoryId => {
    const signalIds = SIGNAL_IDS[categoryId] || [];
    const placeholderCards = signalIds.slice(0, 6).map(() => createSignalCardPlaceholder());
    return createCategorySection(categoryId, placeholderCards, false, []);
  });
  
  // Build CSI widget (replaces old stress war room gauge)
  // Create with initial loading state - will be updated when data loads
  const initialCSIData = {
    date: new Date().toISOString().split('T')[0],
    score: 0,
    regime: 'NORMAL',
    confidence: 0,
    impulse: 0,
    acceleration: 0,
    pillars: {
      FUNDING: { pillar: 'FUNDING', score: 0, signalCount: 0, familyCount: 0, freshFraction: 0, confidence: 0, isDegraded: true, contributions: [] },
      LIQUIDITY: { pillar: 'LIQUIDITY', score: 0, signalCount: 0, familyCount: 0, freshFraction: 0, confidence: 0, isDegraded: true, contributions: [] },
      RATES_CURVE: { pillar: 'RATES_CURVE', score: 0, signalCount: 0, familyCount: 0, freshFraction: 0, confidence: 0, isDegraded: true, contributions: [] },
      CREDIT: { pillar: 'CREDIT', score: 0, signalCount: 0, familyCount: 0, freshFraction: 0, confidence: 0, isDegraded: true, contributions: [] },
      VOL_EQUITY: { pillar: 'VOL_EQUITY', score: 0, signalCount: 0, familyCount: 0, freshFraction: 0, confidence: 0, isDegraded: true, contributions: [] },
      VOL_RATES: { pillar: 'VOL_RATES', score: 0, signalCount: 0, familyCount: 0, freshFraction: 0, confidence: 0, isDegraded: true, contributions: [] },
    },
    topDrivers: [],
    metadata: { signalCount: 0, staleCount: 0, degradedPillars: ['FUNDING', 'LIQUIDITY', 'RATES_CURVE', 'CREDIT', 'VOL_EQUITY', 'VOL_RATES'] }
  };
  
  csiWidgetInstance = createCSIWidget({
    data: initialCSIData,
    compact: false,
    showDrivers: true,
    onDriverClick: (driver) => {
      const signal = pageState.flatSignals[driver.signalId];
      if (signal) openExpandedChartModal(signal, pageState.history[driver.signalId] || []);
    }
  });
  const stressGauge = csiWidgetInstance.element;
  
  // Recession probability gauge
  const recessionGauge = createCompositeGauge({
    id: 'recession-probability',
    title: 'Recession Probability',
    value: 0,
    status: 'calm',
    description: '12-month forward-looking recession odds based on yield curve, credit, and labor signals.',
    components: ['Yield Curve Duration', 'Credit Spreads', 'Claims Trend', 'Real Rates'],
  });
  
  // Initial strength meter and heatmap with empty data
  const initialStrength = { total: ALL_SIGNAL_IDS.length, stressed: 0, elevated: 0, normal: 0, calm: 0, noData: ALL_SIGNAL_IDS.length, loaded: 0 };
  
  // CSI History Chart (replaces strength meter in left column)
  csiHistoryChartInstance = createCSIHistoryChart({
    currentScore: 45, // Initial placeholder
    currentRegime: 'NORMAL'
  });
  
  // Data Freshness Widget
  dataFreshnessInstance = createDataFreshnessWidget();
  
  const initialHeatmap = ALL_SIGNAL_IDS.map(id => ({
    id, 
    abbr: abbreviateSignalId(id),
    name: id, 
    category: 'unknown', 
    level: 'unknown', 
    value: null, 
    hasData: false,
    color: 'var(--tg-text-faint)'
  }));
  // Pass initialStrength to heatmap so footer can render Signal Health bar
  const heatmap = createStressHeatmap(initialHeatmap, (signalId) => {
    const signal = pageState.flatSignals[signalId];
    if (signal) openExpandedChartModal(signal, []);
  }, initialStrength);
  
  // Category tabs
  const categoryTabs = createCategoryTabs('all', handleCategorySelect);
  
  // Search bar
  const searchBar = createSearchBar(handleSearch);
  
  return el('div', { className: 'signals-page' }, [
    el('div', { className: 'signals-bg-layer' }, []),
    el('div', { className: 'signals-overlay' }, []),
    
    el('div', { className: 'signals-page-content' }, [
      // Hero Section
      el('section', { className: 'signals-hero' }, [
        el('p', { className: 'signals-hero-label' }, ['THE WATCHTOWER']),
        el('h1', { className: 'signals-hero-title' }, ['Signals']),
        el('p', { className: 'signals-hero-tagline' }, ['Live Macro Intelligence']),
        el('p', { className: 'signals-hero-description' }, [
          'Proprietary stress indicators derived from Federal Reserve Economic Data. 120 signals across 9 categories tracking liquidity, credit, rates, funding, volatility, and more.'
        ]),
        el('div', { className: 'signals-hero-badge' }, [
          el('span', { className: 'data-source-pill' }, ['⏳ Connecting...']),
          el('span', { className: 'series-count-pill series-count-pill--highlight' }, [`${ALL_SIGNAL_IDS.length} Signals`]),
          el('span', { className: 'series-count-pill' }, [`${CATEGORY_ORDER.length} Categories`]),
          el('button', { 
            className: 'glossary-btn',
            onclick: openGlossaryModal,
          }, [
            el('span', { className: 'glossary-btn__icon' }, ['📖']),
            el('span', { className: 'glossary-btn__label' }, ['Glossary']),
          ]),
        ]),
      ]),
      
      // Signal Intelligence Briefing (Marketing Section)
      createSignalIntelligenceBriefing(),
      
      // Visual Impact Section - Stress Overview
      el('section', { className: 'signals-section stress-overview-section' }, [
        el('div', { className: 'section-header-row' }, [
          el('h2', { className: 'signals-section-title' }, ['Market Stress Overview']),
          el('span', { className: 'section-badge section-badge--live' }, ['LIVE DATA']),
        ]),
        el('div', { className: 'stress-overview-grid' }, [
          el('div', { className: 'stress-overview-left' }, [
            el('div', { className: 'composites-grid' }, [stressGauge, recessionGauge]),
            csiHistoryChartInstance.element,
            dataFreshnessInstance.element,
          ]),
          el('div', { className: 'stress-overview-right' }, [
            heatmap,
          ]),
        ]),
      ]),
      
      // Category Tabs & Search
      el('section', { className: 'signals-section signals-filter-section' }, [
        el('div', { className: 'filter-controls' }, [
          categoryTabs,
          el('div', { className: 'filter-controls__right' }, [
            searchBar,
            el('span', { className: 'visible-count-badge' }, [`${ALL_SIGNAL_IDS.length} Visible`]),
          ]),
        ]),
      ]),
      
      // Signal Categories
      el('section', { className: 'signals-section signals-categories' }, [
        el('div', { className: 'signals-categories-container' }, categorySections),
      ]),
      
      // Methodology Note
      el('section', { className: 'signals-methodology' }, [
        el('div', { className: 'methodology-card' }, [
          el('h2', {}, ['Methodology']),
          el('p', {}, [
            'capitulators.io signals are not financial advice. They represent our interpretation of publicly available Federal Reserve data, designed to identify macro stress conditions before they become obvious. Thresholds are derived from historical analysis but past patterns do not guarantee future results.'
          ]),
          el('p', { className: 'methodology-note' }, [
            'Data refreshes every 5 minutes. Underlying FRED data updates daily with some series having publication lags of 1-2 weeks.'
          ]),
        ]),
      ]),
      
      // Refresh button
      el('div', { className: 'signals-refresh' }, [
        el('button', { 
          className: 'refresh-btn',
          onclick: () => loadSignalData()
        }, ['↻ Refresh Data']),
      ]),
      
      // Back link
      el('div', { className: 'signals-back-link' }, [
        el('a', { href: '#/home' }, ['← Back to Home']),
      ]),
    ]),
  ]);
}

/**
 * Cleanup function for signals page
 */
export function cleanupSignalsPage() {
  ALL_SIGNAL_IDS.forEach(id => destroySignalCard(id));
  
  // Cleanup CSI widget
  if (csiWidgetInstance) {
    csiWidgetInstance.destroy();
    csiWidgetInstance = null;
  }
  
  // Cleanup CSI History Chart
  if (csiHistoryChartInstance) {
    csiHistoryChartInstance.destroy();
    csiHistoryChartInstance = null;
  }
  
  // Cleanup Data Freshness widget
  if (dataFreshnessInstance) {
    dataFreshnessInstance.destroy();
    dataFreshnessInstance = null;
  }
  
  // Cleanup heatmap tooltip
  hideHeatmapTooltip();
  clearTimeout(tooltipTimeout);
  heatmapTooltip = null;
  
  // Cleanup card tooltip (Phase 5)
  hideCardTooltip();
  clearTimeout(cardTooltipTimeout);
  if (cardTooltip) {
    cardTooltip.remove();
    cardTooltip = null;
  }
  
  pageState = { signals: {}, flatSignals: {}, history: {}, loading: true, error: null, lastUpdated: null, activeCategory: 'all', searchQuery: '', stressLevelFilter: null };
}

// Keyboard shortcut for search
document.addEventListener('keydown', (e) => {
  if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
    const searchInput = document.querySelector('.search-bar__input');
    if (searchInput && document.activeElement !== searchInput) {
      e.preventDefault();
      searchInput.focus();
    }
  }
  // ESC to close glossary modal
  if (e.key === 'Escape') {
    closeGlossaryModal();
  }
});

// ============================================================================
// GLOSSARY MODAL
// ============================================================================

let glossaryModalEl = null;
let glossarySearchQuery = '';
let glossaryActiveCategory = 'all';

/**
 * Opens the glossary modal
 */
function openGlossaryModal() {
  if (glossaryModalEl) return;
  
  glossaryModalEl = createGlossaryModal();
  document.body.appendChild(glossaryModalEl);
  
  // Animate in
  requestAnimationFrame(() => {
    glossaryModalEl.classList.add('glossary-modal-overlay--visible');
    glossaryModalEl.querySelector('.glossary-modal').classList.add('glossary-modal--visible');
  });
  
  // Focus search
  setTimeout(() => {
    const searchInput = glossaryModalEl.querySelector('.glossary-search__input');
    if (searchInput) searchInput.focus();
  }, 100);
}

/**
 * Closes the glossary modal
 */
function closeGlossaryModal() {
  if (!glossaryModalEl) return;
  
  glossaryModalEl.classList.remove('glossary-modal-overlay--visible');
  glossaryModalEl.querySelector('.glossary-modal').classList.remove('glossary-modal--visible');
  
  setTimeout(() => {
    if (glossaryModalEl) {
      glossaryModalEl.remove();
      glossaryModalEl = null;
    }
  }, 200);
}

/**
 * Creates the glossary modal
 */
function createGlossaryModal() {
  const overlay = el('div', { 
    className: 'glossary-modal-overlay',
    onclick: (e) => {
      if (e.target === overlay) closeGlossaryModal();
    }
  }, [
    el('div', { className: 'glossary-modal' }, [
      // Header
      el('div', { className: 'glossary-modal__header' }, [
        el('div', { className: 'glossary-modal__title-row' }, [
          el('span', { className: 'glossary-modal__icon' }, ['📖']),
          el('h2', { className: 'glossary-modal__title' }, ['Signal Glossary']),
          el('span', { className: 'glossary-modal__count' }, [`${ALL_SIGNAL_IDS.length} signals`]),
        ]),
        el('button', { 
          className: 'glossary-modal__close',
          onclick: closeGlossaryModal,
          'aria-label': 'Close glossary'
        }, ['×']),
      ]),
      
      // Search & Filters
      el('div', { className: 'glossary-modal__controls' }, [
        el('div', { className: 'glossary-search' }, [
          el('span', { className: 'glossary-search__icon' }, ['🔍']),
          el('input', {
            type: 'text',
            className: 'glossary-search__input',
            placeholder: 'Search signals by name or ID...',
            oninput: (e) => {
              glossarySearchQuery = e.target.value.toLowerCase();
              updateGlossaryList();
            }
          }),
        ]),
        el('div', { className: 'glossary-filters' }, [
          el('button', {
            className: 'glossary-filter glossary-filter--active',
            'data-category': 'all',
            onclick: (e) => selectGlossaryCategory('all', e.target)
          }, ['All']),
          ...CATEGORY_ORDER.map(catId => {
            const cat = CATEGORIES[catId];
            return el('button', {
              className: 'glossary-filter',
              'data-category': catId,
              onclick: (e) => selectGlossaryCategory(catId, e.target)
            }, [`${cat.icon} ${cat.name}`]);
          })
        ]),
      ]),
      
      // Signal List
      el('div', { className: 'glossary-modal__content', 'data-glossary-list': true }, [
        ...createGlossaryList()
      ]),
      
      // Footer
      el('div', { className: 'glossary-modal__footer' }, [
        el('span', { className: 'glossary-modal__hint' }, ['Click any signal for details • ESC to close']),
      ]),
    ]),
  ]);
  
  return overlay;
}

/**
 * Selects a glossary category
 */
function selectGlossaryCategory(category, buttonEl) {
  glossaryActiveCategory = category;
  
  // Update active button
  document.querySelectorAll('.glossary-filter').forEach(btn => {
    btn.classList.remove('glossary-filter--active');
  });
  buttonEl.classList.add('glossary-filter--active');
  
  updateGlossaryList();
}

/**
 * Updates the glossary list based on filters
 */
function updateGlossaryList() {
  const listContainer = document.querySelector('[data-glossary-list]');
  if (!listContainer) return;
  
  listContainer.innerHTML = '';
  createGlossaryList().forEach(item => listContainer.appendChild(item));
}

/**
 * Creates the glossary list items with full documentation
 */
function createGlossaryList() {
  const items = [];
  
  CATEGORY_ORDER.forEach(catId => {
    if (glossaryActiveCategory !== 'all' && glossaryActiveCategory !== catId) return;
    
    const cat = CATEGORIES[catId];
    const signalIds = SIGNAL_IDS[catId] || [];
    
    const filteredSignals = signalIds.filter(id => {
      if (!glossarySearchQuery) return true;
      const meta = SIGNAL_METADATA[id] || {};
      const signalDoc = getSignalDoc(id);
      const name = (signalDoc?.name || meta.name || id).toLowerCase();
      const desc = (signalDoc?.description || meta.description || '').toLowerCase();
      return name.includes(glossarySearchQuery) || 
             id.toLowerCase().includes(glossarySearchQuery) ||
             desc.includes(glossarySearchQuery);
    });
    
    if (filteredSignals.length === 0) return;
    
    // Category header
    items.push(
      el('div', { className: 'glossary-category' }, [
        el('div', { className: 'glossary-category__header' }, [
          el('span', { className: 'glossary-category__icon' }, [cat.icon]),
          el('span', { className: 'glossary-category__name' }, [cat.name]),
          el('span', { className: 'glossary-category__count' }, [`${filteredSignals.length}`]),
        ]),
        el('p', { className: 'glossary-category__desc' }, [cat.shortDesc || cat.description]),
      ])
    );
    
    // Signal items with full documentation
    filteredSignals.forEach(signalId => {
      items.push(createEnhancedGlossaryItem(signalId));
    });
  });
  
  if (items.length === 0) {
    items.push(
      el('div', { className: 'glossary-empty' }, [
        el('span', { className: 'glossary-empty__icon' }, ['🔍']),
        el('p', { className: 'glossary-empty__text' }, ['No signals match your search']),
      ])
    );
  }
  
  return items;
}

/**
 * Creates an enhanced glossary item with full documentation
 */
function createEnhancedGlossaryItem(signalId) {
  const meta = SIGNAL_METADATA[signalId] || {};
  const signal = pageState.flatSignals[signalId];
  const signalDoc = getSignalDoc(signalId);
  const currentValue = signal?.current?.value;
  const currentLevel = signal?.current?.level;
  
  // Build sections array
  const sections = [
    // Header section
    createGlossaryItemHeader(signalId, meta, signal, signalDoc),
    
    // Full description
    signalDoc?.description ? 
      el('div', { className: 'glossary-section glossary-section--description' }, [
        el('p', { className: 'glossary-section__text' }, [signalDoc.description])
      ]) : null,
    
    // Calculation formula
    signalDoc?.calculation ? createFormulaSection(signalDoc.calculation) : null,
    
    // Why It Matters
    signalDoc?.whyItMatters ? createWhyItMattersSection(signalDoc.whyItMatters) : null,
    
    // Market Mechanism
    signalDoc?.marketMechanism ? createMarketMechanismSection(signalDoc.marketMechanism) : null,
    
    // Interpretation grid
    signalDoc?.interpretation ? createInterpretationGrid(signalDoc.interpretation, currentLevel) : null,
    
    // Key Levels
    signalDoc?.keyLevels?.length ? createKeyLevelsSection(signalDoc.keyLevels, currentValue) : null,
    
    // Historical Events
    signalDoc?.historicalEvents?.length ? createHistoricalEventsSection(signalDoc.historicalEvents) : null,
    
    // Trading Implications
    signalDoc?.tradingImplication ? createTradingImplicationSection(signalDoc.tradingImplication) : null,
    
    // Watch For
    signalDoc?.watchFor?.length ? createWatchForSection(signalDoc.watchFor) : null,
    
    // Limitations
    signalDoc?.limitations?.length ? createLimitationsSection(signalDoc.limitations) : null,
    
    // Related Signals
    signalDoc?.relatedSignals?.length ? createRelatedSignalsSection(signalDoc.relatedSignals, signalId) : null,
    
    // Action buttons
    createGlossaryItemActions(signalId, signal)
  ].filter(Boolean);
  
  return el('div', { 
    className: 'glossary-item-expanded',
    'data-signal-id': signalId
  }, sections);
}

/**
 * Creates the header section for a glossary item
 */
function createGlossaryItemHeader(signalId, meta, signal, signalDoc) {
  const currentValue = signal?.current?.value;
  const currentLevel = signal?.current?.level;
  const tier = signalDoc?.tier;
  
  return el('div', { className: 'glossary-item__header' }, [
    el('div', { className: 'glossary-item__header-left' }, [
      el('div', { className: 'glossary-item__title-row' }, [
        el('h3', { className: 'glossary-item__name' }, [signalDoc?.name || meta.name || signalId]),
        tier ? el('span', { 
          className: `glossary-item__tier glossary-item__tier--${tier}`
        }, [getTierLabel(tier)]) : null,
      ].filter(Boolean)),
      el('code', { className: 'glossary-item__id' }, [signalId]),
    ]),
    el('div', { className: 'glossary-item__header-right' }, [
      currentValue != null ? 
        el('div', { className: 'glossary-item__current' }, [
          el('span', { 
            className: `glossary-item__value glossary-item__value--${currentLevel || 'normal'}`
          }, [formatValue(currentValue, meta.units)]),
          currentLevel ? el('span', { 
            className: `glossary-item__level glossary-item__level--${currentLevel}`
          }, [currentLevel.toUpperCase()]) : null,
        ].filter(Boolean)) :
        el('span', { className: 'glossary-item__value glossary-item__value--none' }, ['No data']),
    ]),
  ]);
}

/**
 * Creates the formula section
 */
function createFormulaSection(calculation) {
  return el('div', { className: 'glossary-section glossary-section--formula' }, [
    el('h4', { className: 'glossary-section__title' }, ['📐 Calculation']),
    el('code', { className: 'glossary-formula-code' }, [calculation.formula]),
    calculation.explanation ? 
      el('p', { className: 'glossary-formula-explanation' }, [calculation.explanation]) : null,
    calculation.components?.length ?
      el('div', { className: 'glossary-formula-components' }, [
        el('span', { className: 'glossary-formula-components__label' }, ['Components:']),
        ...calculation.components.map(comp => 
          el('div', { className: 'formula-component' }, [
            el('span', { className: 'formula-component__id' }, [comp.id]),
            el('span', { className: 'formula-component__role' }, [comp.role || comp.name])
          ])
        )
      ]) : null,
  ].filter(Boolean));
}

/**
 * Creates the Why It Matters section
 */
function createWhyItMattersSection(text) {
  return el('div', { className: 'glossary-section glossary-section--why' }, [
    el('h4', { className: 'glossary-section__title' }, ['💡 Why It Matters']),
    el('p', { className: 'glossary-section__text' }, [text])
  ]);
}

/**
 * Creates the Market Mechanism section
 */
function createMarketMechanismSection(text) {
  return el('div', { className: 'glossary-section glossary-section--mechanism' }, [
    el('h4', { className: 'glossary-section__title' }, ['⚙️ Market Mechanism']),
    el('p', { className: 'glossary-section__text' }, [text])
  ]);
}

/**
 * Creates the interpretation grid
 */
function createInterpretationGrid(interpretation, currentLevel) {
  const levels = Object.entries(interpretation);
  
  return el('div', { className: 'glossary-section glossary-section--interpretation' }, [
    el('h4', { className: 'glossary-section__title' }, ['📊 Interpretation']),
    el('div', { className: 'glossary-interpretation-grid' }, 
      levels.map(([level, text]) => {
        const isActive = currentLevel && (level === currentLevel || level.includes(currentLevel));
        return el('div', { 
          className: `glossary-interpretation-card ${isActive ? 'glossary-interpretation-card--active' : ''}`
        }, [
          el('div', { className: 'glossary-interpretation-card__level' }, [level.toUpperCase()]),
          el('p', { className: 'glossary-interpretation-card__text' }, [text])
        ]);
      })
    )
  ]);
}

/**
 * Creates the key levels section
 */
function createKeyLevelsSection(keyLevels, currentValue) {
  return el('div', { className: 'glossary-section glossary-section--levels' }, [
    el('h4', { className: 'glossary-section__title' }, ['📏 Key Levels']),
    el('div', { className: 'glossary-key-levels' }, 
      keyLevels.map(kl => {
        // Check if current value is near this level (within 10%)
        const levelNum = parseFloat(kl.level.replace(/[^0-9.-]/g, ''));
        const isNear = currentValue && !isNaN(levelNum) && 
          Math.abs(currentValue - levelNum) / Math.abs(levelNum) < 0.1;
        
        return el('div', { 
          className: `glossary-key-level ${isNear ? 'glossary-key-level--near' : ''}`
        }, [
          el('span', { className: 'glossary-key-level__value' }, [kl.level]),
          el('span', { className: 'glossary-key-level__significance' }, [kl.significance])
        ]);
      })
    )
  ]);
}

/**
 * Creates the historical events timeline
 */
function createHistoricalEventsSection(events) {
  return el('div', { className: 'glossary-section glossary-section--history' }, [
    el('h4', { className: 'glossary-section__title' }, ['📅 Historical Events']),
    el('div', { className: 'glossary-history-timeline' }, 
      events.map(event => 
        el('div', { className: 'glossary-history-event' }, [
          el('div', { className: 'glossary-history-event__marker' }),
          el('div', { className: 'glossary-history-event__content' }, [
            el('div', { className: 'glossary-history-event__date' }, [event.date]),
            el('div', { className: 'glossary-history-event__title' }, [event.event]),
            el('p', { className: 'glossary-history-event__impact' }, [event.impact])
          ])
        ])
      )
    )
  ]);
}

/**
 * Creates the trading implication section
 */
function createTradingImplicationSection(text) {
  return el('div', { className: 'glossary-section glossary-section--trading' }, [
    el('h4', { className: 'glossary-section__title' }, ['📈 Trading Implications']),
    el('p', { className: 'glossary-section__text' }, [text])
  ]);
}

/**
 * Creates the watch for section
 */
function createWatchForSection(items) {
  return el('div', { className: 'glossary-section glossary-section--watch' }, [
    el('h4', { className: 'glossary-section__title' }, ['👁️ Watch For']),
    el('ul', { className: 'glossary-watch-list' }, 
      items.map(item => 
        el('li', { className: 'glossary-watch-item' }, [item])
      )
    )
  ]);
}

/**
 * Creates the limitations section
 */
function createLimitationsSection(items) {
  return el('div', { className: 'glossary-section glossary-section--limitations' }, [
    el('h4', { className: 'glossary-section__title' }, ['⚠️ Limitations']),
    el('ul', { className: 'glossary-limitations-list' }, 
      items.map(item => 
        el('li', { className: 'glossary-limitation-item' }, [item])
      )
    )
  ]);
}

/**
 * Creates the related signals section with clickable links
 */
function createRelatedSignalsSection(relatedSignals, currentSignalId) {
  return el('div', { className: 'glossary-section glossary-section--related' }, [
    el('h4', { className: 'glossary-section__title' }, ['🔗 Related Signals']),
    el('div', { className: 'glossary-related-signals' }, 
      relatedSignals.map(rel => {
        const relDoc = getSignalDoc(rel.id);
        const relMeta = SIGNAL_METADATA[rel.id] || {};
        
        return el('div', { 
          className: 'glossary-related-signal',
          onclick: (e) => {
            e.stopPropagation();
            // Scroll to related signal in glossary
            const relItem = document.querySelector(`[data-signal-id="${rel.id}"]`);
            if (relItem) {
              relItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
              relItem.classList.add('glossary-item-expanded--highlight');
              setTimeout(() => relItem.classList.remove('glossary-item-expanded--highlight'), 2000);
            }
          }
        }, [
          el('div', { className: 'glossary-related-signal__id' }, [rel.id]),
          el('div', { className: 'glossary-related-signal__name' }, [relDoc?.shortName || relMeta?.name || rel.id]),
          el('div', { className: 'glossary-related-signal__relationship' }, [rel.relationship])
        ]);
      })
    )
  ]);
}

/**
 * Creates action buttons for the glossary item
 */
function createGlossaryItemActions(signalId, signal) {
  return el('div', { className: 'glossary-item__actions' }, [
    el('button', { 
      className: 'glossary-action-btn glossary-action-btn--chart',
      onclick: (e) => {
        e.stopPropagation();
        closeGlossaryModal();
        if (signal) {
          openExpandedChartModal(signal, pageState.history[signalId] || []);
        }
      }
    }, [
      el('span', { className: 'glossary-action-btn__icon' }, ['📈']),
      el('span', { className: 'glossary-action-btn__label' }, ['View Chart'])
    ]),
    signal ? el('button', { 
      className: 'glossary-action-btn glossary-action-btn--scroll',
      onclick: (e) => {
        e.stopPropagation();
        closeGlossaryModal();
        // Find and scroll to signal card on page
        const card = document.querySelector(`[data-signal-id="${signalId}"]`);
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          card.classList.add('signal-card--highlight');
          setTimeout(() => card.classList.remove('signal-card--highlight'), 2000);
        }
      }
    }, [
      el('span', { className: 'glossary-action-btn__icon' }, ['🎯']),
      el('span', { className: 'glossary-action-btn__label' }, ['Find on Page'])
    ]) : null,
  ].filter(Boolean));
}

/**
 * Formats a signal value with appropriate precision
 */
function formatValue(value, units) {
  if (value == null) return '—';
  if (units === '%') return `${value.toFixed(2)}%`;
  if (units === 'σ') return `${value.toFixed(2)}σ`;
  if (units === 'bps') return `${Math.round(value)} bps`;
  if (units === 'B') return `${(value / 1000).toFixed(2)}T`;
  if (units === 'M') return `${(value / 1000000).toFixed(2)}T`;
  if (units === 'K') return `${(value / 1000).toFixed(0)}K`;
  if (typeof value === 'number') {
    return value >= 1000 ? value.toLocaleString() : value.toFixed(2);
  }
  return String(value);
}

export { loadSignalData };
